export type Lang = "en" | "es" | "pt" | "de";

export const LANGS: { code: Lang; label: string }[] = [
    { code: "en", label: "EN" },
    { code: "es", label: "ES" },
    { code: "pt", label: "PT" },
    { code: "de", label: "DE" },
];

export const translations: Record<Lang, Record<string, string>> = {
    en: {
        "lang.name": "English",
        "ui.language": "Language",
        "ui.nav.about": "About",
        "ui.err.generic": "Something went wrong. Please try again.",
        "ui.err.network": "The server cannot be reached. Please check your connection and try again.",
        "ui.err.phaseClosed": "Nominations are currently closed.",
        "ui.err.votingNotActive": "Voting is currently not active.",
        "ui.err.captcha": "The security check failed. Please reload the page and try again.",
        "ui.err.rateLimited": "Too many requests. Please try again in a little while.",
        "ui.err.nominationBlocked": "This nomination is blocked. Please enter something else.",
        "ui.err.nominationLimit": "You have reached the maximum of five nominations in this category.",
        "ui.err.invalidInput": "Please check your entry and try again.",
        "ui.err.categoryNotFound": "This category could not be found.",
        "ui.err.finalistNotFound": "This finalist could not be found.",
        "ui.err.submissionClosed": "The submission deadline has passed.",
        "ui.err.submissionNotOpen": "Submissions are not open yet.",
        "ui.err.invalidFileType": "This file type is not allowed. Allowed: PDF, PPT, PPTX, JPG, PNG, MP4.",
        "ui.err.fileTooLarge": "One of the files exceeds the maximum size of 30 MB.",
        "ui.err.tooManyFiles": "You can upload at most 20 files.",
        "ui.err.uploadFailed": "The upload failed. Please try again.",
        "ui.err.driveUploadFailed": "The files could not be stored. Please try again.",
        "ui.err.noCaseRegistered": "At least one business case must be registered.",
        "ui.err.noCaseSlots": "No remaining case slots are available for this category. Please register additional cases first.",
        "ui.err.registrationNotFound": "This registration could not be found.",
        "ui.err.draftNotFound": "This draft could not be found.",
        "ui.err.businessCaseNotFound": "This business case could not be found.",
        "ui.nav.process": "Process",
        "ui.nav.awards": "Awards",
        "ui.nav.nomination": "Nomination",
        "ui.nav.voting": "Voting",
        "ui.nav.businessCases": "Business Cases",
        "ui.nav.news": "News",
        "hero.eyebrow": "TCG Awards 2026",
        "hero.title": "Vote for the people, products and moments shaping TCG culture.",
        "hero.text": "A global award platform for Trading Card Games, built to recognize excellence across games, creators, events, stores, industry partners and competitive performance.",
        "ui.hero.nominateNow": "Nominate Now",
        "ui.hero.voteNow": "Vote Now",
        "ui.hero.exploreAwards": "Explore Awards",
        "ui.hero.seeTimeline": "See Timeline",
        "ui.hero.categoriesOpen": "Award categories open for recognition",
        "ui.hero.phases": "Phases",
        "ui.hero.globalCommunity": "Global community",
        "ui.cd.nominationEnds": "Nomination ends in",
        "ui.cd.votingEnds": "Voting ends in",
        "ui.cd.nominationStarts": "Nomination starts in",
        "ui.cd.votingStarts": "Voting starts in",
        "ui.cd.noPhase": "No active phase right now",
        "ui.cd.bcOpens": "Business case opens in",
        "ui.cd.bcClosed": "Business case submission closed",
        "ui.cd.bcRemaining": "Remaining For Your Business Case",
        "ui.cd.bcNoDeadline": "No business case deadline set",
        "process.eyebrow": "Vote Timeline",
        "process.title": "How the voting process works",
        "process.text": "The flow is simple: nominate strong entries, narrow them into a shortlist, let the community vote and celebrate the winners.",
        "categories.eyebrow": "Award Categories",
        "categories.title": "All official categories",
        "categories.text": "Every real category from the current TCG Awards structure is included, grouped by voting model.",
        "about.eyebrow": "About the Awards",
        "about.title": "A clearer stage for a growing ecosystem",
        "about.text": "The TCG Awards recognize the people and organizations that move Trading Card Games forward.",
        "business.eyebrow": "Jury Categories",
        "business.title": "Submit a Business Case",
        "business.text": "Companies, agencies, publishers and service providers can submit business cases for jury-based categories such as Brand Partnership, Campaign and Service Provider of the Year.",
        "news.eyebrow": "News & Updates",
        "news.title": "Latest from the TCG Awards",
        "news.text": "Announcements, shortlist reveals and community moments — straight from our social channels.",
        "newsletter.eyebrow": "Stay Updated",
        "newsletter.title": "Follow the road to the winners",
        "newsletter.text": "Get updates around nominations, shortlist announcements, community voting and the final award show.",
        "ui.intro.1a": "Community + Jury", "ui.intro.1b": "Fair voting model",
        "ui.intro.2a": "Brands + Publishers", "ui.intro.2b": "Business cases welcome",
        "ui.intro.3a": "Fans + Players", "ui.intro.3b": "Built for the whole scene",
        "ui.timeline.1.title": "Nomination", "ui.timeline.1.text": "Community, industry and partners bring strong candidates into the race.",
        "ui.timeline.2.title": "Shortlist", "ui.timeline.2.text": "Entries are reviewed and shaped into a clear shortlist for every category.",
        "ui.timeline.3.title": "Community Voting", "ui.timeline.3.text": "Fans and players vote for the community categories that define the scene.",
        "ui.timeline.4.title": "Winners", "ui.timeline.4.text": "The winners are announced and celebrated with the global TCG community.",
        "ui.group.product.title": "Product Excellence", "ui.group.product.subtitle": "Voted by Community",
        "ui.group.community.title": "Community and Creator", "ui.group.community.subtitle": "Voted by Community",
        "ui.group.jury.title": "Industry and Business Excellence", "ui.group.jury.subtitle": "Voted by Jury",
        "ui.group.performance.title": "Performance Excellence", "ui.group.performance.subtitle": "Performance Based Evaluation",
        "ui.about.stat.categories": "Award categories",
        "ui.about.stat.phases": "Phases",
        "ui.about.stat.reach": "Community reach",
        "ui.about.stat.edition": "Inaugural edition",
        "ui.info.1.title": "Why it exists", "ui.info.1.text": "Trading Card Games are culture, creativity, competition and community. The TCG Awards give that ecosystem a dedicated stage.",
        "ui.info.2.title": "How it is decided", "ui.info.2.text": "Community voting meets an independent jury, so passion and professional perspective can shape the outcome together.",
        "ui.info.3.title": "Who it celebrates", "ui.info.3.text": "Games, sets, events, local stores, creators, artists, players, service providers and meaningful partnerships.",
        "ui.business.li1": "Explain the challenge and idea.",
        "ui.business.li2": "Show the execution and impact.",
        "ui.business.li3": "Help the jury evaluate real industry excellence.",
        "ui.business.submit": "Submit Your Case",
        "ui.social.instagramText": "See impressions, announcements and community moments from the TCG Awards.",
        "ui.social.followInstagram": "Follow on Instagram",
        "ui.social.facebookText": "Follow official posts, event updates and announcements for the TCG community.",
        "ui.social.followFacebook": "Follow on Facebook",
        "ui.common.loading": "Loading…",
        "ui.common.continue": "Continue →",
        "ui.common.back": "← Back",
        "ui.common.loadError": "Error while loading.",
        "ui.voting.backHome": "← Back to Home",
        "ui.voting.backHomePage": "← Back to the Home Page",
        "ui.voting.notFoundTitle": "Category not found",
        "ui.voting.notFoundText": "The requested voting category does not exist.",
        "ui.voting.viewWinners": "🏆 View Winners →",
        "ui.voting.notVotable": "This category is not decided by community voting.",
        "ui.voting.idle": "No voting is currently running. Once the nomination or voting phase begins, you can take part here.",
        "ui.voting.voteFavorite": "Vote for your favorite",
        "ui.nom.beforeTitle": "Before you nominate",
        "ui.nom.before1": "This is a community nomination — your voice helps shape the finalists.",
        "ui.nom.before2": "Interim results are not shown publicly.",
        "ui.nom.before3": "Winners are announced on stage at the official event.",
        "ui.nom.yourNominationFor": "Your nomination for",
        "ui.nom.placeholder": "Type your choice…",
        "ui.nom.hint": "Type the name you want to nominate. Exact spelling isn't critical — entries are reviewed and grouped during evaluation.",
        "ui.nom.backInfo": "← Back to info",
        "ui.nom.yourChoice": "Your choice",
        "ui.nom.noEntry": "No entry yet — please go back and type your choice.",
        "ui.nom.security": "Security check",
        "ui.nom.submitting": "Submitting…",
        "ui.nom.submit": "Submit nomination",
        "ui.nom.doneTitle": "Nomination submitted",
        "ui.nom.doneText": "Thank you! Your nomination has been recorded. You can nominate up to five different entries in this category.",
        "ui.nom.another": "Nominate another →",
        "ui.nom.backAll": "← Back to Categories",
        "ui.nom.errGeneric": "Something went wrong. Please try again.",
        "ui.nom.errSubmit": "Could not submit your vote. Please try again.",
        "ui.vote.noFinalists": "No finalists have been set for this category yet.",
        "ui.vote.alreadyVoted": "You have already voted in this category. Your current choice is highlighted — you can change it until the phase ends.",
        "ui.vote.yourVote": "Your vote",
        "ui.vote.doneTitle": "Vote submitted",
        "ui.vote.doneText": "Thank you! Your vote has been recorded. You can change it at any time while voting is open.",
        "ui.vote.saved": "✓ Your vote has been saved.",
        "ui.vote.saving": "Saving…",
        "ui.vote.change": "Change vote",
        "ui.vote.cast": "Vote",
        "ui.vote.saveError": "Could not save your vote.",
        "ui.winners.title": "Winners",
        "ui.winners.pending": "The winners will be published after the award show.",
        "ui.winners.picture": "Picture",
        "ui.winners.game": "Game",
        "ui.winners.points": "Points",
        "ui.winners.pts": "pts",
        "ui.winners.place": "Place",
                "bc.nav.aria": "Business case navigation",
        "bc.nav.home": "Home",
        "bc.nav.awards": "Awards",
        "bc.nav.submit": "Submit",
        "bc.nav.businessCase": "Business Case",

        "bc.step.overview.title": "Overview",
        "bc.step.overview.subtitle": "Review the categories and jury guidance",
        "bc.step.registration.title": "Registration",
        "bc.step.registration.subtitle": "Company, billing, and contact",
        "bc.step.category.title": "Category",
        "bc.step.category.subtitle": "Choose the case you are describing",
        "bc.step.case.title": "Case Information",
        "bc.step.case.subtitle": "Describe the business case itself",
        "bc.step.terms.title": "Terms",
        "bc.step.terms.subtitle": "Accept the required conditions",
        "bc.step.attachments.title": "Attachments",
        "bc.step.attachments.subtitle": "Upload supporting material",
        "bc.step.review.title": "Review",
        "bc.step.review.subtitle": "Check everything before submitting",

        "bc.help.title": "Need help?",
        "bc.help.text": "Review the submission guidance, fill in the registration, then describe the case itself and upload the supporting material for the jury.",
        "bc.help.note": "Use Save as draft any time to generate a personal resume link.",

        "bc.draft.status": "Draft status",
        "bc.draft.loading": "Loading saved draft...",

        "bc.action.saving": "Saving...",
        "bc.action.toRegistration": "Continue to Registration",
        "bc.action.toCategory": "Continue to Category",
        "bc.action.toCase": "Continue to Case Information",
        "bc.action.toTerms": "Continue to Terms",
        "bc.action.toAttachments": "Continue to Attachments",
        "bc.action.toReview": "Continue to Review",
        "bc.action.submit": "Submit Business Case",
        "bc.action.continue": "Continue",
        "bc.overview.title": "Submit a Business Case",
        "bc.overview.text": "Review the three jury categories and the guidance below, then complete the registration and describe your case. You can prepare multiple cases across the categories.",
        "bc.overview.ctaText": "Ready to begin? Start with the registration and work through each step at your own pace.",
        "bc.overview.ctaButton": "Start Registration",
        "bc.cat.brand.title": "Brand Partnership of the Year",
        "bc.cat.brand.description": "Celebrate the most impactful collaboration between brands by recognizing partnerships that elevated creativity, reach, and cultural relevance.",
        "bc.cat.brand.s1.title": "Basic Information",
        "bc.cat.brand.s1.description": "Provide the partnership name, participating brands or organizations, duration, and targeted markets or regions.",
        "bc.cat.brand.s2.title": "Strategic Context",
        "bc.cat.brand.s2.description": "Describe the partnership objective, the challenge or opportunity it addressed, and the target audience it was built for.",
        "bc.cat.brand.s3.title": "Partnership Structure",
        "bc.cat.brand.s3.description": "Explain the structure, scope, and roles involved, for example licensing, co-branding, distribution, retail, event, or creator collaborations.",
        "bc.cat.brand.s4.title": "Impact Metrics",
        "bc.cat.brand.s4.description": "Show revenue, reach, participation, engagement, earned media, or other metrics that prove the impact of the partnership.",
        "bc.cat.brand.s5.title": "Strategic Value",
        "bc.cat.brand.s5.description": "Highlight how the partnership creates long-term value for the brands, the game, and the broader TCG ecosystem.",
        "bc.cat.campaign.title": "Campaign of the Year",
        "bc.cat.campaign.description": "Recognize the most outstanding marketing campaign in the TCG industry with a strong combination of strategy, storytelling, and measurable impact.",
        "bc.cat.campaign.s1.title": "Basic Information",
        "bc.cat.campaign.s1.description": "Provide the campaign name, participating brands or organizations, duration, and targeted markets or regions.",
        "bc.cat.campaign.s2.title": "Campaign Objectives",
        "bc.cat.campaign.s2.description": "Explain why the campaign was launched, which audience it targeted, and what the core creative concept was.",
        "bc.cat.campaign.s3.title": "Campaign Execution",
        "bc.cat.campaign.s3.description": "Describe measures, channels, creator partnerships, events, retail activations, and content formats used.",
        "bc.cat.campaign.s4.title": "Impact Metrics",
        "bc.cat.campaign.s4.description": "Share business impact, reach, participation, engagement, media coverage, and other success metrics.",
        "bc.cat.campaign.s5.title": "Strategic Value",
        "bc.cat.campaign.s5.description": "Describe the campaign's long-term impact and how it created value for the TCG player and fan base.",
        "bc.cat.service.title": "Service Provider of the Year",
        "bc.cat.service.description": "Celebrate the platform, app, or service that made life easier for players, stores, and organizers and strengthened the TCG ecosystem.",
        "bc.cat.service.s1.title": "Basic Information",
        "bc.cat.service.s1.description": "Name the service, platform, participating brands or organizations, and the markets or regions it serves.",
        "bc.cat.service.s2.title": "Service Overview",
        "bc.cat.service.s2.description": "Describe the core functionality, active category, target audience, and which industry problem the service solves.",
        "bc.cat.service.s3.title": "Product and Execution",
        "bc.cat.service.s3.description": "Explain features, integrations, scalability, reliability, and how customer support or service delivery works.",
        "bc.cat.service.s4.title": "Adoption, Usage, and Impact Metrics",
        "bc.cat.service.s4.description": "Show how success is measured, how many people use the service, and what business impact it creates.",
        "bc.cat.service.s5.title": "Strategic Value",
        "bc.cat.service.s5.description": "Highlight long-term relevance, innovation, differentiation, and why the service matters for the wider industry.",
        "bc.common.total": "total",
        "bc.common.of": "of",
        "bc.common.case": "case",
        "bc.common.cases": "cases",
        "bc.common.selectCountry": "Select a country",
        "bc.common.pleaseChoose": "Please choose",
        "bc.common.yes": "Yes",
        "bc.common.no": "No",
        "bc.reg.step.title": "Registration details",
        "bc.reg.step.text": "Add the company, billing, contact, and category volume information before you move on to the actual business case.",
        "bc.reg.selected": "Cases currently selected",
        "bc.reg.alreadyPrepared": "Already prepared",
        "bc.reg.sec.general": "General company information",
        "bc.reg.sec.billing": "Billing address",
        "bc.reg.sec.contact": "Contact person",
        "bc.reg.sec.volume": "Case volume by category",
        "bc.reg.f.companyName": "Company name",
        "bc.reg.f.vatId": "VAT identification number",
        "bc.reg.f.street": "Street and house number",
        "bc.reg.f.postcode": "Postcode",
        "bc.reg.f.city": "City",
        "bc.reg.f.country": "Country",
        "bc.reg.f.generalPhone": "General phone number",
        "bc.reg.f.generalEmail": "General e-mail address",
        "bc.reg.f.diffBilling": "Is there a different billing address?",
        "bc.reg.f.billingCompany": "Billing company name",
        "bc.reg.f.billingStreet": "Billing street and house number",
        "bc.reg.f.billingPostcode": "Billing postcode",
        "bc.reg.f.billingCity": "Billing city",
        "bc.reg.f.billingCountry": "Billing country",
        "bc.reg.f.contactFirst": "First name of contact person",
        "bc.reg.f.contactLast": "Surname of contact person",
        "bc.reg.f.salutation": "Salutation",
        "bc.reg.f.contactPhone": "Phone number of contact person",
        "bc.reg.f.contactEmail": "E-mail address of contact person",
        "bc.reg.sal.female": "Female",
        "bc.reg.sal.nonbinary": "Non-binary",
        "bc.reg.sal.male": "Male",
        "bc.reg.p.companyName": "Complete and legally valid company name",
        "bc.reg.p.vatId": "Required for companies from EU countries",
        "bc.reg.p.street": "Street name and house number",
        "bc.reg.p.postcode": "Postal code",
        "bc.reg.p.city": "City",
        "bc.reg.p.billingCompany": "Billing company name",
        "bc.reg.p.billingStreet": "Billing street and house number",
        "bc.reg.p.billingPostcode": "Billing postcode",
        "bc.reg.p.billingCity": "Billing city",
        "bc.reg.p.contactFirst": "First name",
        "bc.reg.p.contactLast": "Surname",
        "bc.country.germany": "Germany",
        "bc.country.austria": "Austria",
        "bc.country.switzerland": "Switzerland",
        "bc.country.netherlands": "Netherlands",
        "bc.country.belgium": "Belgium",
        "bc.country.france": "France",
        "bc.country.italy": "Italy",
        "bc.country.spain": "Spain",
        "bc.country.uk": "United Kingdom",
        "bc.country.us": "United States",
        "bc.country.canada": "Canada",
        "bc.country.other": "Other",
        "bc.cat3.step.title": "Choose the category for this case",
        "bc.cat3.step.text": "Now pick the exact category for the business case you are currently describing. The descriptions below stay aligned with the first overview page.",
        "bc.cat3.warn.increase": "Increase the case count for this category in the registration step or choose one of the categories you already selected.",
        "bc.cat3.warn.allPrepared": "All registered cases for this category have already been prepared. Choose another category with remaining slots.",
        "bc.cat3.notRegistered": "Not registered",
        "bc.cat3.remainingOf": "remaining of",
        "bc.case.step.title": "Describe the business case",
        "bc.case.step.text": "This is the actual heart of the submission. Give the jury enough context, strategy, execution detail, and measurable impact to understand why this case matters.",
        "bc.case.process.title": "Submission and voting process",
        "bc.case.process.p1": "After submitting this form, Merz Verlag GmbH & Co. KG will issue an invoice for the submission fee.",
        "bc.case.process.p2": "With that invoice you will get a separate link for submitting the information of the case(s). These information needs to be submitted until 30 June 2026.",
        "bc.case.process.p3": "If cases are submitted after that date or the dedicated invoice has not been paid, they will not be passed to the jury and will not be considered.",
        "bc.case.process.p4": "From 1 to 31 August 2026 the jury will review all cases and vote the Top 3. After that all participating companies will be informed and the Top 3 will be invited to the TCG Awards Gala. The Gala will take place on Thursday, 22 October 2026, during SPIEL Essen 2026.",
        "bc.case.selectedCategory": "Currently selected category",
        "bc.case.remaining": "Remaining cases in this category",
        "bc.case.f.title": "Case title",
        "bc.case.f.shortDescription": "Short description",
        "bc.case.f.challenge": "Strategic context / challenge",
        "bc.case.f.objectives": "Objectives",
        "bc.case.f.execution": "Execution",
        "bc.case.f.impactMetrics": "Impact metrics",
        "bc.case.f.strategicValue": "Strategic value",
        "bc.case.p.title": "Give your case a clear, compelling title",
        "bc.case.p.shortDescription": "Summarize your case in up to 300 characters",
        "bc.case.p.challenge": "Describe the starting point, challenge, or opportunity",
        "bc.case.p.objectives": "What did you want to achieve and for whom?",
        "bc.case.p.execution": "Describe the implementation, measures, channels, and actions",
        "bc.case.p.impactMetrics": "Add measurable results, traction, media impact, revenue, usage, or adoption data",
        "bc.case.p.strategicValue": "Explain the long-term value for the brand, product, audience, or wider TCG ecosystem",
        "bc.cat.brand.short": "Brand Partnership",
        "bc.cat.campaign.short": "Campaign",
        "bc.cat.service.short": "Service Provider",
        "bc.reg.noCasesSelected": "No cases selected yet",
        "bc.nav.backHome": "Back to Home",
        "bc.nav.back": "Back",
        "bc.terms.step.title": "Participation terms",
        "bc.terms.step.text": "Review the required conditions before uploading your files. Every business case is billed separately and can still be saved as a draft with your personal link.",
        "bc.terms.fee.label": "Submission fee",
        "bc.terms.fee.price": "500 € per business case",
        "bc.terms.fee.note": "Net price. VAT will be added when the invoice is issued.",
        "bc.terms.required": "Required",
        "bc.terms.net.title": "Net prices",
        "bc.terms.net.text": "All prices stated in this submission are net prices. The respective VAT will be added when the invoice is issued.",
        "bc.terms.net.accept": "I understand that all prices stated in this form are net prices.",
        "bc.terms.conditions.title": "Conditions of participation",
        "bc.terms.conditions.text": "The conditions of participation should be read carefully and in their entirety.",
        "bc.terms.conditions.link": "Conditions of Participation (opens in new tab)",
        "bc.terms.conditions.accept": "I have read the conditions of participation and accept them in their entirety.",
        "bc.terms.offer.title": "Legally binding offer",
        "bc.terms.offer.text": "By submitting this form, a legally binding offer is sent to Merz Verlag as representative of the organizing parties as listed in §2 in the conditions of participation. Merz Verlag as representative of the organizing parties can accept this offer by sending the invoice. Only then is a legally binding contract concluded.",
        "bc.terms.organizers.title": "Organizers",
        "bc.terms.organizers.text": "The TCG Awards are jointly organized by Merz Verlag and their partners as listed in §2 in the conditions of participation.",
        "bc.terms.phone": "Phone: 0201/8436207-0",
        "bc.terms.openTab": "opens in new tab",
        "bc.terms.admin.title": "Administrative organization:",
        "bc.terms.admin.text": "Technical handling of the application process and the invoicing is managed by Merz Verlag GmbH & Co. KG",
        "bc.terms.offer.accept": "I understand that by submitting this form, a legally binding offer is being made to the organizers. A legally valid contract is concluded with the invoice from the organizers.",
        "bc.terms.cancel.title": "Cancellation fee",
        "bc.terms.cancel.p1": "A request for cancellation must be submitted in writing to",
        "bc.terms.cancel.p2": "Cancellation is possible prior to invoicing and is free of charge.",
        "bc.terms.cancel.p3": "Cancellation of Brand Case submissions after invoicing is possible and is subject to a processing fee of €50. If the invoice has not yet been paid, the original invoice will be canceled and a new invoice for the cancellation fee will be issued.",
        "bc.terms.cancel.p4": "If the invoice has already been paid, the amount already paid will be refunded minus the processing fee.",
        "bc.terms.cancel.p5": "Cancellations after July 31, 2026, are not permitted.",
        "bc.terms.cancel.accept": "I understand and accept the cancellation policy.",
        "bc.terms.privacy.title": "Privacy policy",
        "bc.terms.privacy.p1": "I hereby confirm that Merz Verlag GmbH & Co. KG may use and store the data I have provided for the purpose of processing.",
        "bc.terms.privacy.p2": "The data will only be used for internal processing by Merz Verlag GmbH & Co. KG, Card Sports League GmbH and Hana Sports & Entertainment GmbH and will not be passed on to third parties without consent.",
        "bc.terms.privacy.p3": "Further information on our data processing can be found in our privacy policy:",
        "bc.terms.privacy.accept": "I hereby agree.",
        "bc.attachments.step.title": "Attachments",
        "bc.attachments.step.text": "Upload supporting assets such as decks, reports, creatives, visuals, screenshots, or videos that make the business case easier to evaluate.",
        "bc.attachments.uploadLabel": "Upload files",
        "bc.attachments.dropzoneTitle": "Drag files here or click to upload",
        "bc.attachments.dropzoneHelp": "PDF, PPT, PPTX, JPG, PNG, MP4 - max. 30 MB per file",
        "bc.attachments.uploadedTitle": "Uploaded files",
        "bc.attachments.file": "file",
        "bc.attachments.files": "files",
        "bc.attachments.restoredWarning": "Attachments were restored from draft metadata. Please re-upload the original files before submitting so they can be saved to Google Drive.",
        "bc.attachments.empty": "No files uploaded yet.",
        "bc.attachments.remove": "Remove",
        "bc.attachments.invalid": "Please upload files in PDF, PPT, PPTX, JPG, PNG, or MP4 format. Maximum 30 MB per file.",
        "bc.review.step.title": "Review",
        "bc.review.step.text": "Check the registration data, the chosen category, the actual business-case description, and the uploaded files before you submit.",
        "bc.review.registration": "Registration",
        "bc.review.edit": "Edit",
        "bc.review.noAddress": "No address provided yet",
        "bc.review.contact": "Contact",
        "bc.review.caseVolume": "Case volume",
        "bc.review.preparedSoFar": "Prepared so far",
        "bc.review.category": "Category",
        "ui.voting.winnersAvailable": "The voting phase has ended successfully. The winners are now available to view.",
        "bc.review.businessCase": "Business case",
        "bc.review.noShortDescription": "No short description added yet.",
        "bc.review.noChallenge": "No strategic context added yet.",
        "bc.review.noObjectives": "No objectives added yet.",
        "bc.review.noExecution": "No execution details added yet.",
        "bc.review.noImpactMetrics": "No impact metrics added yet.",
        "bc.review.noStrategicValue": "No strategic value added yet.",
        "bc.review.feeNet": "500 € per business case, net",
        "bc.review.termsAccepted": "Net prices, conditions of participation, legally binding offer, cancellation fee, and privacy policy accepted.",
        "bc.review.uploaded": "uploaded",
        "bc.draft.save": "Save as draft",
        "bc.draft.saving": "Saving draft...",
        "bc.draft.close": "Close draft dialog",
        "bc.draft.title": "Save as draft",
        "bc.draft.text": "Continue the form later or share this private link with someone who should finish it.",
        "bc.draft.link": "Draft link",
        "bc.draft.copy": "Copy link",
        "bc.draft.note": "Uploaded files are restored as references. They may need to be uploaded again later for the final submission.",
        "bc.draft.saved": "Draft saved successfully. Use the link below to continue later.",
        "bc.draft.copied": "Draft link copied to your clipboard.",
        "bc.draft.copyFailed": "Copying failed. Please copy the draft link manually.",
        "bc.draft.noLink": "The draft link could not be generated.",
        "bc.error.loadDraft": "Could not load the saved draft.",
        "bc.error.saveDraft": "Could not save the draft.",
        "bc.error.saveBusinessCase": "Could not save the business case.",
        "bc.error.unknownBackend": "Unknown backend error.",
        "bc.success.thanks": "Thank you!",
        "bc.success.title": "Your business case has been saved successfully.",
        "bc.success.text": "Your registration is now in the system and ready for the next internal review step.",
        "bc.success.moreTitle": "More business cases are still open",
        "bc.success.registered": "You registered",
        "bc.success.inTotal": "in total and have already prepared",
        "bc.success.remaining": "There are still",
        "bc.success.toComplete": "to complete from the same registration.",
        "bc.success.details": "Submission details",
        "bc.success.submissionNumber": "Submission number",
        "bc.success.registrationId": "Registration ID",
        "bc.success.category": "Category",
        "bc.success.company": "Company",
        "bc.success.contactEmail": "Contact e-mail",
        "bc.success.submissionDate": "Submission date",
        "bc.success.progress": "Progress",
        "bc.success.prepared": "prepared",
        "bc.success.createNext": "Create next business case",
        "bc.success.startAnother": "Start another submission",
        "bc.success.returnHome": "Return to Home",
        "bc.validation.summaryStart": "Please correct",
        "bc.validation.error": "error",
        "bc.validation.errors": "errors",
        "bc.validation.summaryEnd": "before continuing:",
        "bc.validation.categoryRequired": "Please choose a category for the business case you are describing.",
        "bc.validation.categoryRemaining": "Please choose a category with at least one registered case remaining.",
        "bc.validation.uploadBefore": "Please upload at least one attachment before you",
        "bc.validation.action.continue": "continue",
        "bc.validation.action.submit": "submit",
        "bc.validation.reupload": "Please re-upload restored attachment files before submitting, so the originals can be saved to Google Drive.",
        "bc.validation.requiredCaseField": "Please complete the required case field:",
        "bc.validation.requiredField": "Please complete the required field:",
        "bc.validation.validGeneralEmail": "Please enter a valid general e-mail address.",
        "bc.validation.validContactEmail": "Please enter a valid contact e-mail address.",
        "bc.validation.validGeneralPhone": "General phone number must contain 6 to 15 digits and no letters.",
        "bc.validation.validContactPhone": "Contact phone number must contain 6 to 15 digits and no letters.",
        "bc.validation.atLeastOneCase": "Please select at least one case across the three Business Case categories.",
        "bc.validation.acceptNet": "Please accept the required net prices confirmation.",
        "bc.validation.acceptConditions": "Please accept the required conditions of participation.",
        "bc.validation.acceptOffer": "Please accept the required legally binding offer confirmation.",
        "bc.validation.acceptCancellation": "Please accept the required cancellation fee confirmation.",
        "bc.validation.acceptPrivacy": "Please accept the required privacy policy confirmation.",
        "ui.footer.tagline": "Official TCG Awards platform",
        "ui.footer.follow": "Follow us",
        "ui.footer.privacy": "Privacy",
        "ui.footer.imprint": "Imprint",

        "ui.newsletter.heading": "Newsletter",
        "ui.newsletter.firstName": "First Name*",
        "ui.newsletter.lastName": "Last Name*",
        "ui.newsletter.media": "Media / Corporate Name",
        "ui.newsletter.link": "Link to Media / Channel / Website",
        "ui.newsletter.country": "Country",
        "ui.newsletter.email": "Email*",
        "ui.newsletter.privacyPre": "With this you agree to storage and usage of your personal data in accordance with our ",
        "ui.newsletter.privacyLink": "privacy policy",
        "ui.newsletter.privacyPost": ".",
        "ui.newsletter.subscribe": "Subscribe",

        "bc.guide.evaluation.title": "Evaluation Criteria",
        "bc.guide.evaluation.text1": "Not every text field must be filled with the same depth, but the jury should still be able to understand the scope, the strategic value, and the measurable impact of the case. Relevant metrics, supporting data, and well-structured attachments make the evaluation much easier.",
        "bc.guide.evaluation.text2": "Optional supporting material such as decks, videos, links, screenshots, or one-pagers can strengthen the submission further. If you are not ready yet, save the form as a draft and continue later with your personal link.",
        "bc.guide.questions": "Questions?",
    },
    es: {
        "lang.name": "Español",
        "ui.language": "Idioma",
        "ui.nav.about": "Acerca de",
        "ui.nav.process": "Proceso",
        "ui.nav.awards": "Premios",
        "ui.nav.nomination": "Nominación",
        "ui.nav.voting": "Votación",
        "ui.nav.businessCases": "Casos de Negocio",
        "ui.nav.news": "Noticias",
        "hero.eyebrow": "TCG Awards 2026",
        "hero.title": "Vota por las personas, productos y momentos que definen la cultura TCG.",
        "hero.text": "Una plataforma global de premios para Juegos de Cartas Coleccionables, creada para reconocer la excelencia en juegos, creadores, eventos, tiendas, socios de la industria y rendimiento competitivo.",
        "ui.hero.nominateNow": "Nominar ahora",
        "ui.hero.voteNow": "Votar ahora",
        "ui.hero.exploreAwards": "Explorar premios",
        "ui.cd.nominationEnds": "La nominación termina en",
        "ui.cd.votingEnds": "La votación termina en",
        "ui.cd.nominationStarts": "La nominación comienza en",
        "ui.cd.votingStarts": "La votación comienza en",
        "ui.cd.noPhase": "No hay ninguna fase activa ahora mismo",
        "ui.cd.bcOpens": "El caso de negocio abre en",
        "ui.cd.bcClosed": "Envío de casos de negocio cerrado",
        "ui.cd.bcRemaining": "Restante para tu caso de negocio",
        "ui.cd.bcNoDeadline": "No hay fecha límite de caso de negocio",
        "ui.hero.seeTimeline": "Ver cronograma",
        "ui.hero.categoriesOpen": "Categorías de premios abiertas al reconocimiento",
        "ui.hero.phases": "Fases",
        "ui.err.noCaseSlots": "No quedan plazas disponibles para esta categoría. Registra casos adicionales primero.",
        "ui.hero.globalCommunity": "Comunidad global",
        "process.eyebrow": "Cronograma de votación",
        "process.title": "Cómo funciona el proceso de votación",
        "process.text": "El flujo es simple: nomina candidaturas sólidas, redúcelas a una lista de finalistas, deja que la comunidad vote y celebra a los ganadores.",
        "categories.eyebrow": "Categorías de premios",
        "categories.title": "Todas las categorías oficiales",
        "categories.text": "Se incluyen todas las categorías reales de la estructura actual de los TCG Awards, agrupadas por modelo de votación.",
        "about.eyebrow": "Sobre los premios",
        "about.title": "Un escenario más claro para un ecosistema en crecimiento",
        "about.text": "Los TCG Awards reconocen a las personas y organizaciones que impulsan los Juegos de Cartas Coleccionables.",
        "business.eyebrow": "Categorías del jurado",
        "business.title": "Presenta un caso de negocio",
        "business.text": "Empresas, agencias, editoriales y proveedores de servicios pueden presentar casos de negocio para las categorías evaluadas por el jurado, como Alianza de Marca, Campaña y Proveedor de Servicios del Año.",
        "news.eyebrow": "Noticias y novedades",
        "news.title": "Lo último de los TCG Awards",
        "news.text": "Anuncios, revelaciones de finalistas y momentos de la comunidad, directamente desde nuestras redes sociales.",
        "newsletter.eyebrow": "Mantente al día",
        "newsletter.title": "Sigue el camino hacia los ganadores",
        "newsletter.text": "Recibe novedades sobre nominaciones, anuncios de finalistas, votación de la comunidad y la gala final de premios.",
        "ui.intro.1a": "Comunidad + Jurado", "ui.intro.1b": "Modelo de votación justo",
        "ui.intro.2a": "Marcas + Editoriales", "ui.intro.2b": "Casos de negocio bienvenidos",
        "ui.intro.3a": "Fans + Jugadores", "ui.intro.3b": "Hecho para toda la escena",
        "ui.timeline.1.title": "Nominación", "ui.timeline.1.text": "La comunidad, la industria y los socios traen candidatos sólidos a la competición.",
        "ui.timeline.2.title": "Lista de finalistas", "ui.timeline.2.text": "Las candidaturas se revisan y se convierten en una lista clara de finalistas por categoría.",
        "ui.timeline.3.title": "Votación de la comunidad", "ui.timeline.3.text": "Los fans y jugadores votan en las categorías comunitarias que definen la escena.",
        "ui.timeline.4.title": "Ganadores", "ui.timeline.4.text": "Los ganadores se anuncian y celebran con la comunidad global de TCG.",
        "ui.group.product.title": "Excelencia de Producto", "ui.group.product.subtitle": "Votado por la comunidad",
        "ui.group.community.title": "Comunidad y Creadores", "ui.group.community.subtitle": "Votado por la comunidad",
        "ui.group.jury.title": "Excelencia Industrial y Empresarial", "ui.group.jury.subtitle": "Votado por el jurado",
        "ui.group.performance.title": "Excelencia en Rendimiento", "ui.group.performance.subtitle": "Evaluación basada en rendimiento",
        "ui.about.stat.categories": "Categorías de premios",
        "ui.voting.winnersAvailable": "La fase de votación terminó correctamente. Los ganadores ya están disponibles.",
        "ui.about.stat.phases": "Fases",
        "ui.about.stat.reach": "Alcance comunitario",
        "ui.about.stat.edition": "Edición inaugural",
        "ui.info.1.title": "Por qué existe", "ui.info.1.text": "Los Juegos de Cartas Coleccionables son cultura, creatividad, competición y comunidad. Los TCG Awards dan a ese ecosistema un escenario propio.",
        "ui.info.2.title": "Cómo se decide", "ui.info.2.text": "La votación de la comunidad se une a un jurado independiente, para que la pasión y la perspectiva profesional den forma al resultado juntas.",
        "ui.info.3.title": "A quién celebra", "ui.info.3.text": "Juegos, sets, eventos, tiendas locales, creadores, artistas, jugadores, proveedores de servicios y alianzas significativas.",
        "ui.business.li1": "Explica el reto y la idea.",
        "ui.business.li2": "Muestra la ejecución y el impacto.",
        "ui.business.li3": "Ayuda al jurado a evaluar la excelencia real de la industria.",
        "ui.business.submit": "Presenta tu caso",
        "ui.social.instagramText": "Mira impresiones, anuncios y momentos de la comunidad de los TCG Awards.",
        "ui.social.followInstagram": "Seguir en Instagram",
        "ui.social.facebookText": "Sigue las publicaciones oficiales, novedades de eventos y anuncios para la comunidad TCG.",
        "ui.social.followFacebook": "Seguir en Facebook",
        "ui.common.loading": "Cargando…",
        "ui.common.continue": "Continuar →",
        "ui.common.back": "← Atrás",
        "ui.common.loadError": "Error al cargar.",
        "ui.voting.backHome": "← Volver al inicio",
        "ui.voting.backHomePage": "← Volver a la página de inicio",
        "ui.voting.notFoundTitle": "Categoría no encontrada",
        "ui.voting.notFoundText": "La categoría de votación solicitada no existe.",
        "ui.voting.viewWinners": "🏆 Ver ganadores →",
        "ui.voting.notVotable": "Esta categoría no se decide por votación de la comunidad.",
        "ui.voting.idle": "Actualmente no hay ninguna votación en curso. Cuando comience la fase de nominación o votación, podrás participar aquí.",
        "ui.voting.voteFavorite": "Vota por tu favorito",
        "ui.nom.beforeTitle": "Antes de nominar",
        "ui.nom.before1": "Esta es una nominación de la comunidad: tu voz ayuda a definir a los finalistas.",
        "ui.nom.before2": "Los resultados provisionales no se muestran públicamente.",
        "ui.nom.before3": "Los ganadores se anuncian en el escenario del evento oficial.",
        "ui.nom.yourNominationFor": "Tu nominación para",
        "ui.nom.placeholder": "Escribe tu elección…",
        "ui.nom.hint": "Escribe el nombre que quieres nominar. La ortografía exacta no es crítica: las entradas se revisan y agrupan durante la evaluación.",
        "ui.nom.backInfo": "← Volver a la información",
        "ui.nom.yourChoice": "Tu elección",
        "ui.nom.noEntry": "Aún no hay ninguna entrada: vuelve atrás y escribe tu elección.",
        "ui.nom.security": "Verificación de seguridad",
        "ui.nom.submitting": "Enviando…",
        "ui.nom.submit": "Enviar nominación",
        "ui.nom.doneTitle": "Nominación enviada",
        "ui.nom.doneText": "Gracias! tu nominación ha sido registrada. Puedes nominar hasta cinco entradas diferentes en esta categoría.",
        "ui.nom.another": "Nominar otra →",
        "ui.nom.backAll": "← Volver a categorías",
        "ui.nom.errGeneric": "Algo salió mal. Inténtalo de nuevo.",
        "ui.nom.errSubmit": "No se pudo enviar tu voto. Inténtalo de nuevo.",
        "ui.vote.doneTitle": "Voto enviado",
        "ui.vote.doneText": "Gracias! tu voto ha sido registrado. Puedes cambiarlo en cualquier momento mientras la votación esté abierta.",
        "ui.vote.noFinalists": "Aún no se han definido finalistas para esta categoría.",
        "ui.vote.alreadyVoted": "Ya has votado en esta categoría. Tu elección actual está resaltada: puedes cambiarla hasta que termine la fase.",
        "ui.vote.yourVote": "Tu voto",
        "ui.vote.saved": "✓ Tu voto se ha guardado.",
        "ui.vote.saving": "Guardando…",
        "ui.vote.change": "Cambiar voto",
        "ui.vote.cast": "Votar",
        "ui.vote.saveError": "No se pudo guardar tu voto.",
        "ui.winners.title": "Ganadores",
        "ui.winners.pending": "Los ganadores se publicarán después de la gala de premios.",
        "ui.winners.picture": "Imagen",
        "ui.winners.game": "Juego",
        "ui.winners.points": "Puntos",
        "ui.winners.pts": "pts",
        "ui.winners.place": "Puesto",
        "bc.nav.aria": "Navegación del caso de negocio",
        "bc.nav.home": "Inicio",
        "bc.nav.awards": "Premios",
        "bc.nav.submit": "Enviar",
        "bc.nav.businessCase": "Caso de negocio",
        "bc.step.overview.title": "Resumen",
        "bc.step.overview.subtitle": "Revisa las categorías y la guía del jurado",
        "bc.step.registration.title": "Registro",
        "bc.step.registration.subtitle": "Empresa, facturación y contacto",
        "bc.step.category.title": "Categoría",
        "bc.step.category.subtitle": "Elige el caso que estás describiendo",
        "bc.step.case.title": "Información del caso",
        "bc.step.case.subtitle": "Describe el caso de negocio",
        "bc.step.terms.title": "Condiciones",
        "bc.step.terms.subtitle": "Acepta las condiciones requeridas",
        "bc.step.attachments.title": "Archivos adjuntos",
        "bc.step.attachments.subtitle": "Sube material de apoyo",
        "bc.step.review.title": "Revisión",
        "bc.step.review.subtitle": "Comprueba todo antes de enviar",
        "bc.help.title": "¿Necesitas ayuda?",
        "bc.help.text": "Revisa la guía de envío, completa el registro, describe el caso y sube el material de apoyo para el jurado.",
        "bc.help.note": "Usa Guardar como borrador en cualquier momento para generar un enlace personal de continuación.",
        "bc.draft.status": "Estado del borrador",
        "bc.draft.loading": "Cargando borrador guardado...",

        "bc.action.saving": "Guardando...",
        "bc.action.toRegistration": "Continuar al registro",
        "bc.action.toCategory": "Continuar a la categoría",
        "bc.action.toCase": "Continuar a la información del caso",
        "bc.action.toTerms": "Continuar a las condiciones",
        "bc.action.toAttachments": "Continuar a los archivos adjuntos",
        "bc.action.toReview": "Continuar a la revisión",
        "bc.action.submit": "Enviar caso de negocio",
        "bc.action.continue": "Continuar",
        "bc.overview.title": "Envía un caso de negocio",
        "bc.overview.text": "Revisa las tres categorías del jurado y la guía a continuación, luego completa el registro y describe tu caso. Puedes preparar varios casos en las distintas categorías.",
        "bc.overview.ctaText": "¿Listo para empezar? Comienza con el registro y avanza por cada paso a tu propio ritmo.",
        "bc.overview.ctaButton": "Iniciar registro",
        "bc.cat.brand.title": "Colaboración de marca del año",
        "bc.cat.brand.description": "Celebra la colaboración más impactante entre marcas reconociendo alianzas que elevaron la creatividad, el alcance y la relevancia cultural.",
        "bc.cat.brand.s1.title": "Información básica",
        "bc.cat.brand.s1.description": "Indica el nombre de la alianza, las marcas u organizaciones participantes, la duración y los mercados o regiones objetivo.",
        "bc.cat.brand.s2.title": "Contexto estratégico",
        "bc.cat.brand.s2.description": "Describe el objetivo de la alianza, el reto u oportunidad que abordó y el público objetivo para el que se creó.",
        "bc.cat.brand.s3.title": "Estructura de la alianza",
        "bc.cat.brand.s3.description": "Explica la estructura, el alcance y los roles implicados, por ejemplo licencias, co-branding, distribución, retail, eventos o colaboraciones con creadores.",
        "bc.cat.brand.s4.title": "Métricas de impacto",
        "bc.cat.brand.s4.description": "Muestra ingresos, alcance, participación, interacción, medios ganados u otras métricas que demuestren el impacto de la alianza.",
        "bc.cat.brand.s5.title": "Valor estratégico",
        "bc.cat.brand.s5.description": "Destaca cómo la alianza genera valor a largo plazo para las marcas, el juego y el ecosistema TCG en general.",
        "bc.cat.campaign.title": "Campaña del año",
        "bc.cat.campaign.description": "Reconoce la campaña de marketing más destacada del sector TCG, con una sólida combinación de estrategia, narrativa e impacto medible.",
        "bc.cat.campaign.s1.title": "Información básica",
        "bc.cat.campaign.s1.description": "Indica el nombre de la campaña, las marcas u organizaciones participantes, la duración y los mercados o regiones objetivo.",
        "bc.cat.campaign.s2.title": "Objetivos de la campaña",
        "bc.cat.campaign.s2.description": "Explica por qué se lanzó la campaña, a qué público se dirigió y cuál fue el concepto creativo central.",
        "bc.cat.campaign.s3.title": "Ejecución de la campaña",
        "bc.cat.campaign.s3.description": "Describe las acciones, los canales, las colaboraciones con creadores, los eventos, las activaciones en retail y los formatos de contenido utilizados.",
        "bc.cat.campaign.s4.title": "Métricas de impacto",
        "bc.cat.campaign.s4.description": "Comparte el impacto de negocio, el alcance, la participación, la interacción, la cobertura mediática y otras métricas de éxito.",
        "bc.cat.campaign.s5.title": "Valor estratégico",
        "bc.cat.campaign.s5.description": "Describe el impacto a largo plazo de la campaña y cómo generó valor para la comunidad de jugadores y fans de TCG.",
        "bc.cat.service.title": "Proveedor de servicios del año",
        "bc.cat.service.description": "Celebra la plataforma, la app o el servicio que facilitó la vida a jugadores, tiendas y organizadores y fortaleció el ecosistema TCG.",
        "bc.cat.service.s1.title": "Información básica",
        "bc.cat.service.s1.description": "Nombra el servicio, la plataforma, las marcas u organizaciones participantes y los mercados o regiones que atiende.",
        "bc.cat.service.s2.title": "Descripción del servicio",
        "bc.cat.service.s2.description": "Describe la funcionalidad principal, la categoría activa, el público objetivo y qué problema del sector resuelve el servicio.",
        "bc.cat.service.s3.title": "Producto y ejecución",
        "bc.cat.service.s3.description": "Explica las funciones, las integraciones, la escalabilidad, la fiabilidad y cómo funciona la atención al cliente o la prestación del servicio.",
        "bc.cat.service.s4.title": "Métricas de adopción, uso e impacto",
        "bc.cat.service.s4.description": "Muestra cómo se mide el éxito, cuántas personas usan el servicio y qué impacto de negocio genera.",
        "bc.cat.service.s5.title": "Valor estratégico",
        "bc.cat.service.s5.description": "Destaca la relevancia a largo plazo, la innovación, la diferenciación y por qué el servicio es importante para el sector en general.",
        "bc.common.total": "en total",
        "bc.common.of": "de",
        "bc.common.case": "caso",
        "bc.common.cases": "casos",
        "bc.common.selectCountry": "Selecciona un país",
        "bc.common.pleaseChoose": "Elige una opción",
        "bc.common.yes": "Sí",
        "bc.common.no": "No",
        "bc.reg.step.title": "Datos de registro",
        "bc.reg.step.text": "Añade la información de la empresa, facturación, contacto y volumen por categoría antes de pasar al caso de negocio en sí.",
        "bc.reg.selected": "Casos seleccionados actualmente",
        "bc.reg.alreadyPrepared": "Ya preparados",
        "bc.reg.sec.general": "Información general de la empresa",
        "bc.reg.sec.billing": "Dirección de facturación",
        "bc.reg.sec.contact": "Persona de contacto",
        "bc.reg.sec.volume": "Volumen de casos por categoría",
        "bc.reg.f.companyName": "Nombre de la empresa",
        "bc.reg.f.vatId": "Número de identificación fiscal (IVA)",
        "bc.reg.f.street": "Calle y número",
        "bc.reg.f.postcode": "Código postal",
        "bc.reg.f.city": "Ciudad",
        "bc.reg.f.country": "País",
        "bc.reg.f.generalPhone": "Teléfono general",
        "bc.reg.f.generalEmail": "Correo electrónico general",
        "bc.reg.f.diffBilling": "¿Hay una dirección de facturación diferente?",
        "bc.reg.f.billingCompany": "Nombre de la empresa de facturación",
        "bc.reg.f.billingStreet": "Calle y número de facturación",
        "bc.reg.f.billingPostcode": "Código postal de facturación",
        "bc.reg.f.billingCity": "Ciudad de facturación",
        "bc.reg.f.billingCountry": "País de facturación",
        "bc.reg.f.contactFirst": "Nombre de la persona de contacto",
        "bc.reg.f.contactLast": "Apellidos de la persona de contacto",
        "bc.reg.f.salutation": "Tratamiento",
        "bc.reg.f.contactPhone": "Teléfono de la persona de contacto",
        "bc.reg.f.contactEmail": "Correo electrónico de la persona de contacto",
        "bc.reg.sal.female": "Femenino",
        "bc.reg.sal.nonbinary": "No binario",
        "bc.reg.sal.male": "Masculino",
        "bc.reg.p.companyName": "Nombre completo y legalmente válido de la empresa",
        "bc.reg.p.vatId": "Obligatorio para empresas de países de la UE",
        "bc.reg.p.street": "Nombre de la calle y número",
        "bc.reg.p.postcode": "Código postal",
        "bc.reg.p.city": "Ciudad",
        "bc.reg.p.billingCompany": "Nombre de la empresa de facturación",
        "bc.reg.p.billingStreet": "Calle y número de facturación",
        "bc.reg.p.billingPostcode": "Código postal de facturación",
        "bc.reg.p.billingCity": "Ciudad de facturación",
        "bc.reg.p.contactFirst": "Nombre",
        "bc.reg.p.contactLast": "Apellidos",
        "bc.country.germany": "Alemania",
        "bc.country.austria": "Austria",
        "bc.country.switzerland": "Suiza",
        "bc.country.netherlands": "Países Bajos",
        "bc.country.belgium": "Bélgica",
        "bc.country.france": "Francia",
        "bc.country.italy": "Italia",
        "bc.country.spain": "España",
        "bc.country.uk": "Reino Unido",
        "bc.country.us": "Estados Unidos",
        "bc.country.canada": "Canadá",
        "bc.country.other": "Otro",
        "bc.cat3.step.title": "Elige la categoría para este caso",
        "bc.cat3.step.text": "Ahora elige la categoría exacta del caso de negocio que estás describiendo. Las descripciones de abajo coinciden con la página de resumen inicial.",
        "bc.cat3.warn.increase": "Aumenta el número de casos de esta categoría en el paso de registro o elige una de las categorías que ya seleccionaste.",
        "bc.cat3.warn.allPrepared": "Todos los casos registrados de esta categoría ya se han preparado. Elige otra categoría con plazas disponibles.",
        "bc.cat3.notRegistered": "No registrado",
        "bc.cat3.remainingOf": "restantes de",
        "bc.case.step.title": "Describe el caso de negocio",
        "bc.case.step.text": "Este es el verdadero núcleo de la candidatura. Ofrece al jurado suficiente contexto, estrategia, detalles de ejecución e impacto medible para que entienda por qué este caso importa.",
        "bc.case.process.title": "Proceso de candidatura y votación",
        "bc.case.process.p1": "Tras enviar este formulario, Merz Verlag GmbH & Co. KG emitirá una factura por la tarifa de candidatura.",
        "bc.case.process.p2": "Con esa factura recibirás un enlace independiente para enviar la información del caso o los casos. Esta información debe enviarse antes del 30 de junio de 2026.",
        "bc.case.process.p3": "Si los casos se envían después de esa fecha o la factura correspondiente no se ha pagado, no se remitirán al jurado y no se tendrán en cuenta.",
        "bc.case.process.p4": "Del 1 al 31 de agosto de 2026 el jurado revisará todos los casos y votará el Top 3. Después se informará a todas las empresas participantes y el Top 3 será invitado a la TCG Awards Gala. La gala tendrá lugar el jueves 22 de octubre de 2026, durante SPIEL Essen 2026.",
        "bc.case.selectedCategory": "Categoría seleccionada actualmente",
        "bc.case.remaining": "Casos restantes en esta categoría",
        "bc.case.f.title": "Título del caso",
        "bc.case.f.shortDescription": "Descripción breve",
        "bc.case.f.challenge": "Contexto estratégico / desafío",
        "bc.case.f.objectives": "Objetivos",
        "bc.case.f.execution": "Ejecución",
        "bc.case.f.impactMetrics": "Métricas de impacto",
        "bc.case.f.strategicValue": "Valor estratégico",
        "bc.case.p.title": "Dale a tu caso un título claro y atractivo",
        "bc.case.p.shortDescription": "Resume tu caso en un máximo de 300 caracteres",
        "bc.case.p.challenge": "Describe el punto de partida, el desafío o la oportunidad",
        "bc.case.p.objectives": "¿Qué querías lograr y para quién?",
        "bc.case.p.execution": "Describe la implementación, las medidas, los canales y las acciones",
        "bc.case.p.impactMetrics": "Añade resultados medibles, tracción, impacto en medios, ingresos, uso o datos de adopción",
        "bc.case.p.strategicValue": "Explica el valor a largo plazo para la marca, el producto, el público o el ecosistema TCG en general",
        "bc.cat.brand.short": "Colaboración de marca",
        "bc.cat.campaign.short": "Campaña",
        "bc.cat.service.short": "Proveedor de servicios",
        "bc.reg.noCasesSelected": "Aún no hay casos seleccionados",
        "bc.nav.backHome": "Volver al inicio",
        "bc.nav.back": "Atrás",
        "bc.terms.step.title": "Condiciones de participación",
        "bc.terms.step.text": "Revisa las condiciones requeridas antes de subir tus archivos. Cada caso de negocio se factura por separado y puede guardarse como borrador con tu enlace personal.",
        "bc.terms.fee.label": "Tarifa de candidatura",
        "bc.terms.fee.price": "500 € por caso de negocio",
        "bc.terms.fee.note": "Precio neto. El IVA se añadirá cuando se emita la factura.",
        "bc.terms.required": "Obligatorio",
        "bc.terms.net.title": "Precios netos",
        "bc.terms.net.text": "Todos los precios indicados en esta candidatura son precios netos. El IVA correspondiente se añadirá cuando se emita la factura.",
        "bc.terms.net.accept": "Entiendo que todos los precios indicados en este formulario son precios netos.",
        "bc.terms.conditions.title": "Condiciones de participación",
        "bc.terms.conditions.text": "Las condiciones de participación deben leerse cuidadosamente y en su totalidad.",
        "bc.terms.conditions.link": "Condiciones de participación (se abre en una nueva pestaña)",
        "bc.terms.conditions.accept": "He leído las condiciones de participación y las acepto en su totalidad.",
        "bc.terms.offer.title": "Oferta jurídicamente vinculante",
        "bc.terms.offer.text": "Al enviar este formulario, se envía una oferta jurídicamente vinculante a Merz Verlag como representante de las partes organizadoras indicadas en el §2 de las condiciones de participación. Merz Verlag, como representante de las partes organizadoras, puede aceptar esta oferta enviando la factura. Solo entonces se concluye un contrato jurídicamente vinculante.",
        "bc.terms.organizers.title": "Organizadores",
        "bc.terms.organizers.text": "Los TCG Awards son organizados conjuntamente por Merz Verlag y sus socios indicados en el §2 de las condiciones de participación.",
        "bc.terms.phone": "Teléfono: 0201/8436207-0",
        "bc.terms.openTab": "se abre en una nueva pestaña",
        "bc.terms.admin.title": "Organización administrativa:",
        "bc.terms.admin.text": "La gestión técnica del proceso de candidatura y la facturación está a cargo de Merz Verlag GmbH & Co. KG",
        "bc.terms.offer.accept": "Entiendo que al enviar este formulario realizo una oferta jurídicamente vinculante a los organizadores. El contrato válido se concluye con la factura de los organizadores.",
        "bc.terms.cancel.title": "Tarifa de cancelación",
        "bc.terms.cancel.p1": "Una solicitud de cancelación debe enviarse por escrito a",
        "bc.terms.cancel.p2": "La cancelación es posible antes de la facturación y es gratuita.",
        "bc.terms.cancel.p3": "La cancelación de candidaturas de Brand Case después de la facturación es posible y está sujeta a una tarifa de gestión de 50 €. Si la factura aún no se ha pagado, se cancelará la factura original y se emitirá una nueva factura por la tarifa de cancelación.",
        "bc.terms.cancel.p4": "Si la factura ya se ha pagado, se reembolsará el importe abonado menos la tarifa de gestión.",
        "bc.terms.cancel.p5": "No se permiten cancelaciones después del 31 de julio de 2026.",
        "bc.terms.cancel.accept": "Entiendo y acepto la política de cancelación.",
        "bc.terms.privacy.title": "Política de privacidad",
        "bc.terms.privacy.p1": "Por la presente confirmo que Merz Verlag GmbH & Co. KG puede utilizar y almacenar los datos que he proporcionado con fines de procesamiento.",
        "bc.terms.privacy.p2": "Los datos solo se utilizarán para el procesamiento interno por Merz Verlag GmbH & Co. KG, Card Sports League GmbH y Hana Sports & Entertainment GmbH y no se transmitirán a terceros sin consentimiento.",
        "bc.terms.privacy.p3": "Encontrarás más información sobre nuestro tratamiento de datos en nuestra política de privacidad:",
        "bc.terms.privacy.accept": "Por la presente doy mi consentimiento.",
        "bc.attachments.step.title": "Archivos adjuntos",
        "bc.attachments.step.text": "Sube materiales de apoyo como presentaciones, informes, creatividades, visuales, capturas de pantalla o videos que ayuden a evaluar el caso de negocio.",
        "bc.attachments.uploadLabel": "Subir archivos",
        "bc.attachments.dropzoneTitle": "Arrastra archivos aquí o haz clic para subirlos",
        "bc.attachments.dropzoneHelp": "PDF, PPT, PPTX, JPG, PNG, MP4 - máx. 30 MB por archivo",
        "bc.attachments.uploadedTitle": "Archivos subidos",
        "bc.attachments.file": "archivo",
        "bc.attachments.files": "archivos",
        "bc.attachments.restoredWarning": "Los archivos adjuntos se restauraron desde los metadatos del borrador. Vuelve a subir los archivos originales antes de enviar para que puedan guardarse en Google Drive.",
        "bc.attachments.empty": "Aún no se han subido archivos.",
        "bc.attachments.remove": "Eliminar",
        "bc.attachments.invalid": "Sube archivos en formato PDF, PPT, PPTX, JPG, PNG o MP4. Máximo 30 MB por archivo.",
        "bc.review.step.title": "Revisión",
        "bc.review.step.text": "Revisa los datos de registro, la categoría elegida, la descripción del caso de negocio y los archivos subidos antes de enviar.",
        "bc.review.registration": "Registro",
        "bc.review.edit": "Editar",
        "bc.review.noAddress": "Aún no se ha indicado ninguna dirección",
        "bc.review.contact": "Contacto",
        "bc.review.caseVolume": "Volumen de casos",
        "bc.review.preparedSoFar": "Preparados hasta ahora",
        "bc.review.category": "Categoría",
        "bc.review.businessCase": "Caso de negocio",
        "bc.review.noShortDescription": "Aún no se ha añadido una descripción breve.",
        "bc.review.noChallenge": "Aún no se ha añadido contexto estratégico.",
        "bc.review.noObjectives": "Aún no se han añadido objetivos.",
        "bc.review.noExecution": "Aún no se han añadido detalles de ejecución.",
        "bc.review.noImpactMetrics": "Aún no se han añadido métricas de impacto.",
        "bc.review.noStrategicValue": "Aún no se ha añadido valor estratégico.",
        "bc.review.feeNet": "500 € por caso de negocio, neto",
        "bc.review.termsAccepted": "Precios netos, condiciones de participación, oferta jurídicamente vinculante, tarifa de cancelación y política de privacidad aceptadas.",
        "bc.review.uploaded": "subidos",
        "bc.draft.save": "Guardar como borrador",
        "bc.draft.saving": "Guardando borrador...",
        "bc.draft.close": "Cerrar diálogo de borrador",
        "bc.draft.title": "Guardar como borrador",
        "bc.draft.text": "Continúa el formulario más tarde o comparte este enlace privado con alguien que deba completarlo.",
        "bc.draft.link": "Enlace del borrador",
        "bc.draft.copy": "Copiar enlace",
        "bc.draft.note": "Los archivos subidos se restauran como referencias. Es posible que deban subirse de nuevo más adelante para el envío final.",
        "bc.draft.saved": "Borrador guardado correctamente. Usa el enlace de abajo para continuar más tarde.",
        "bc.draft.copied": "El enlace del borrador se copió al portapapeles.",
        "bc.draft.copyFailed": "No se pudo copiar. Copia el enlace del borrador manualmente.",
        "bc.draft.noLink": "No se pudo generar el enlace del borrador.",
        "bc.error.loadDraft": "No se pudo cargar el borrador guardado.",
        "bc.error.saveDraft": "No se pudo guardar el borrador.",
        "bc.error.saveBusinessCase": "No se pudo guardar el caso de negocio.",
        "bc.error.unknownBackend": "Error desconocido del backend.",
        "bc.success.thanks": "¡Gracias!",
        "bc.success.title": "Tu caso de negocio se ha guardado correctamente.",
        "bc.success.text": "Tu registro ya está en el sistema y listo para el siguiente paso de revisión interna.",
        "bc.success.moreTitle": "Aún hay más casos de negocio abiertos",
        "bc.success.registered": "Has registrado",
        "bc.success.inTotal": "en total y ya has preparado",
        "bc.success.remaining": "Aún quedan",
        "bc.success.toComplete": "por completar desde el mismo registro.",
        "bc.success.details": "Detalles del envío",
        "bc.success.submissionNumber": "Número de envío",
        "bc.success.registrationId": "ID de registro",
        "bc.success.category": "Categoría",
        "bc.success.company": "Empresa",
        "ui.err.generic": "Algo salió mal. Inténtalo de nuevo.",
        "ui.err.network": "No se puede contactar con el servidor. Comprueba tu conexión e inténtalo de nuevo.",
        "ui.err.phaseClosed": "Las nominaciones están cerradas actualmente.",
        "ui.err.votingNotActive": "La votación no está activa actualmente.",
        "ui.err.captcha": "La verificación de seguridad ha fallado. Recarga la página e inténtalo de nuevo.",
        "ui.err.rateLimited": "Demasiadas solicitudes. Inténtalo más tarde.",
        "ui.err.nominationBlocked": "Esta entrada no está permitida para la nominación.",
        "ui.err.nominationLimit": "Has alcanzado el máximo de nominaciones en esta categoría.",
        "ui.err.invalidInput": "Revisa los datos introducidos e inténtalo de nuevo.",
        "ui.err.categoryNotFound": "Esta categoría no existe.",
        "ui.err.finalistNotFound": "Este finalista no existe.",
        "ui.err.submissionClosed": "El plazo de entrega ha finalizado.",
        "ui.err.submissionNotOpen": "El plazo de entrega aún no está abierto.",
        "ui.err.invalidFileType": "Este tipo de archivo no está permitido. Permitidos: PDF, PPT, PPTX, JPG, PNG, MP4.",
        "ui.err.fileTooLarge": "Uno de los archivos supera el tamaño máximo de 30 MB.",
        "ui.err.tooManyFiles": "Puedes subir un máximo de 20 archivos.",
        "ui.err.uploadFailed": "La subida ha fallado. Inténtalo de nuevo.",
        "ui.err.driveUploadFailed": "No se pudieron guardar los archivos. Inténtalo de nuevo.",
        "ui.err.noCaseRegistered": "Debe registrarse al menos un caso de negocio.",
        "ui.err.registrationNotFound": "No se ha encontrado este registro.",
        "ui.err.draftNotFound": "No se ha encontrado este borrador.",
        "ui.err.businessCaseNotFound": "No se ha encontrado este caso de negocio.",
        "bc.success.contactEmail": "Correo electrónico de contacto",
        "bc.success.submissionDate": "Fecha de envío",
        "bc.success.progress": "Progreso",
        "bc.success.prepared": "preparados",
        "bc.success.createNext": "Crear siguiente caso de negocio",
        "bc.success.startAnother": "Iniciar otro envío",
        "bc.success.returnHome": "Volver al inicio",
        "bc.validation.summaryStart": "Corrige",
        "bc.validation.error": "error",
        "bc.validation.errors": "errores",
        "bc.validation.summaryEnd": "antes de continuar:",
        "bc.validation.categoryRequired": "Elige una categoría para el caso de negocio que estás describiendo.",
        "bc.validation.categoryRemaining": "Elige una categoría con al menos un caso registrado restante.",
        "bc.validation.uploadBefore": "Sube al menos un archivo adjunto antes de",
        "bc.validation.action.continue": "continuar",
        "bc.validation.action.submit": "enviar",
        "bc.validation.reupload": "Vuelve a subir los archivos adjuntos restaurados antes de enviar, para que los originales puedan guardarse en Google Drive.",
        "bc.validation.requiredCaseField": "Completa el campo obligatorio del caso:",
        "bc.validation.requiredField": "Completa el campo obligatorio:",
        "bc.validation.validGeneralEmail": "Introduce un correo electrónico general válido.",
        "bc.validation.validContactEmail": "Introduce un correo electrónico de contacto válido.",
        "bc.validation.validGeneralPhone": "El teléfono general debe contener de 6 a 15 dígitos y no debe contener letras.",
        "bc.validation.validContactPhone": "El teléfono de contacto debe contener de 6 a 15 dígitos y no debe contener letras.",
        "bc.validation.atLeastOneCase": "Selecciona al menos un caso entre las tres categorías de casos de negocio.",
        "bc.validation.acceptNet": "Acepta la confirmación requerida sobre precios netos.",
        "bc.validation.acceptConditions": "Acepta las condiciones de participación requeridas.",
        "bc.validation.acceptOffer": "Acepta la confirmación requerida sobre la oferta jurídicamente vinculante.",
        "bc.validation.acceptCancellation": "Acepta la confirmación requerida sobre la tarifa de cancelación.",
        "bc.validation.acceptPrivacy": "Acepta la política de privacidad requerida.",
        "ui.footer.tagline": "Plataforma oficial de los TCG Awards",
        "ui.footer.follow": "Síguenos",
        "ui.footer.privacy": "Privacidad",
        "ui.footer.imprint": "Aviso legal",

        "ui.newsletter.heading": "Boletín",
        "ui.newsletter.firstName": "Nombre*",
        "ui.newsletter.lastName": "Apellidos*",
        "ui.newsletter.media": "Medio / Nombre de la empresa",
        "ui.newsletter.link": "Enlace al medio / canal / sitio web",
        "ui.newsletter.country": "País",
        "ui.newsletter.email": "Correo electrónico*",
        "ui.newsletter.privacyPre": "Al hacerlo, aceptas el almacenamiento y uso de tus datos personales de acuerdo con nuestra ",
        "ui.newsletter.privacyLink": "política de privacidad",
        "ui.newsletter.privacyPost": ".",
        "ui.newsletter.subscribe": "Suscribirse",

        "bc.guide.evaluation.title": "Criterios de evaluación",
        "bc.guide.evaluation.text1": "No todos los campos de texto deben rellenarse con la misma profundidad, pero el jurado debe poder entender el alcance, el valor estratégico y el impacto medible del caso. Las métricas relevantes, los datos de apoyo y los archivos adjuntos bien estructurados facilitan mucho la evaluación.",
        "bc.guide.evaluation.text2": "El material de apoyo opcional, como presentaciones, vídeos, enlaces, capturas de pantalla o resúmenes de una página, puede reforzar aún más la propuesta. Si aún no estás listo, guarda el formulario como borrador y continúa más tarde con tu enlace personal.",
        "bc.guide.questions": "¿Preguntas?",
    },
    pt: {
        "lang.name": "Português",
        "ui.language": "Idioma",
        "ui.nav.about": "Sobre",
        "ui.nav.process": "Processo",
        "ui.nav.awards": "Prêmios",
        "ui.nav.nomination": "Indicação",
        "ui.nav.voting": "Votação",
        "ui.nav.businessCases": "Casos de Negócio",
        "ui.nav.news": "Notícias",
        "hero.eyebrow": "TCG Awards 2026",
        "hero.title": "Vote nas pessoas, produtos e momentos que moldam a cultura TCG.",
        "hero.text": "Uma plataforma global de prêmios para Jogos de Cartas Colecionáveis, criada para reconhecer a excelência em jogos, criadores, eventos, lojas, parceiros da indústria e desempenho competitivo.",
        "ui.hero.nominateNow": "Indicar agora",
        "ui.hero.voteNow": "Votar agora",
        "ui.hero.exploreAwards": "Explorar Awards",
        "ui.hero.seeTimeline": "Ver cronograma",
        "ui.hero.categoriesOpen": "Categorias de prêmios abertas para reconhecimento",
        "ui.hero.phases": "Fases",
        "ui.hero.globalCommunity": "Comunidade global",
        "ui.cd.nominationEnds": "A indicação termina em",
        "ui.cd.votingEnds": "A votação termina em",
        "ui.cd.nominationStarts": "A indicação começa em",
        "ui.cd.votingStarts": "A votação começa em",
        "ui.cd.noPhase": "Nenhuma fase ativa no momento",
        "ui.cd.bcOpens": "O caso de negócio abre em",
        "ui.cd.bcClosed": "Envio de casos de negócio encerrado",
        "ui.cd.bcRemaining": "Restante para o seu caso de negócio",
        "ui.cd.bcNoDeadline": "Nenhum prazo de caso de negócio definido",
        "process.eyebrow": "Cronograma de votação",
        "process.title": "Como funciona o processo de votação",
        "process.text": "O fluxo é simples: indique candidaturas fortes, reduza-as a uma lista de finalistas, deixe a comunidade votar e celebre os vencedores.",
        "categories.eyebrow": "Categorias de prêmios",
        "categories.title": "Todas as categorias oficiais",
        "categories.text": "Todas as categorias reais da estrutura atual dos TCG Awards estão incluídas, agrupadas por modelo de votação.",
        "about.eyebrow": "Sobre os prêmios",
        "about.title": "Um palco mais claro para um ecossistema em crescimento",
        "about.text": "Os TCG Awards reconhecem as pessoas e organizações que impulsionam os Jogos de Cartas Colecionáveis.",
        "business.eyebrow": "Categorias do júri",
        "business.title": "Envie um caso de negócio",
        "business.text": "Empresas, agências, editoras e prestadores de serviços podem enviar casos de negócio para as categorias avaliadas pelo júri, como Parceria de Marca, Campanha e Prestador de Serviços do Ano.",
        "news.eyebrow": "Notícias e novidades",
        "news.title": "O mais recente dos TCG Awards",
        "news.text": "Anúncios, revelações de finalistas e momentos da comunidade, diretamente das nossas redes sociais.",
        "newsletter.eyebrow": "Fique por dentro",
        "newsletter.title": "Acompanhe o caminho até os vencedores",
        "newsletter.text": "Receba novidades sobre indicações, anúncios de finalistas, votação da comunidade e a cerimônia final de premiação.",
        "ui.intro.1a": "Comunidade + Júri", "ui.intro.1b": "Modelo de votação justo",
        "ui.intro.2a": "Marcas + Editoras", "ui.intro.2b": "Casos de negócio bem-vindos",
        "ui.intro.3a": "Fãs + Jogadores", "ui.intro.3b": "Feito para toda a cena",
        "ui.timeline.1.title": "Indicação", "ui.timeline.1.text": "A comunidade, a indústria e os parceiros trazem candidatos fortes para a disputa.",
        "ui.timeline.2.title": "Lista de finalistas", "ui.timeline.2.text": "As candidaturas são revisadas e transformadas numa lista clara de finalistas por categoria.",
        "ui.timeline.3.title": "Votação da comunidade", "ui.timeline.3.text": "Fãs e jogadores votam nas categorias comunitárias que definem a cena.",
        "ui.timeline.4.title": "Vencedores", "ui.timeline.4.text": "Os vencedores são anunciados e celebrados com a comunidade global de TCG.",
        "ui.group.product.title": "Excelência de Produto", "ui.group.product.subtitle": "Votado pela comunidade",
        "ui.group.community.title": "Comunidade e Criadores", "ui.group.community.subtitle": "Votado pela comunidade",
        "ui.group.jury.title": "Excelência Industrial e Empresarial", "ui.group.jury.subtitle": "Votado pelo júri",
        "ui.group.performance.title": "Excelência em Desempenho", "ui.group.performance.subtitle": "Avaliação baseada em desempenho",
        "ui.about.stat.categories": "Categorias de prêmios",
        "ui.about.stat.phases": "Fases",
        "ui.err.noCaseSlots": "Não há vagas disponíveis para esta categoria. Registe primeiro casos adicionais.",
        "ui.err.generic": "Algo correu mal. Tente novamente.",
        "ui.err.network": "Não é possível contactar o servidor. Verifique a sua ligação e tente novamente.",
        "ui.err.phaseClosed": "As indicações estão atualmente encerradas.",
        "ui.err.votingNotActive": "A votação não está ativa neste momento.",
        "ui.err.captcha": "A verificação de segurança falhou. Recarregue a página e tente novamente.",
        "ui.err.rateLimited": "Demasiados pedidos. Tente novamente mais tarde.",
        "ui.err.nominationBlocked": "Esta entrada não é permitida para indicação.",
        "ui.err.nominationLimit": "Atingiu o número máximo de indicações nesta categoria.",
        "ui.err.invalidInput": "Verifique os dados introduzidos e tente novamente.",
        "ui.err.categoryNotFound": "Esta categoria não existe.",
        "ui.err.finalistNotFound": "Este finalista não existe.",
        "ui.err.submissionClosed": "O prazo de submissão terminou.",
        "ui.err.submissionNotOpen": "As submissões ainda não estão abertas.",
        "ui.err.invalidFileType": "Este tipo de ficheiro não é permitido. Permitidos: PDF, PPT, PPTX, JPG, PNG, MP4.",
        "ui.err.fileTooLarge": "Um dos ficheiros excede o tamanho máximo de 30 MB.",
        "ui.err.tooManyFiles": "Pode carregar no máximo 20 ficheiros.",
        "ui.err.uploadFailed": "O carregamento falhou. Tente novamente.",
        "ui.err.driveUploadFailed": "Não foi possível guardar os ficheiros. Tente novamente.",
        "ui.err.noCaseRegistered": "É necessário registar pelo menos um caso de negócio.",
        "ui.err.registrationNotFound": "Este registo não foi encontrado.",
        "ui.err.draftNotFound": "Este rascunho não foi encontrado.",
        "ui.err.businessCaseNotFound": "Este caso de negócio não foi encontrado.",
        "ui.about.stat.reach": "Alcance da comunidade",
        "ui.about.stat.edition": "Edição inaugural",
        "ui.voting.winnersAvailable": "A fase de votação terminou com sucesso. Os vencedores já estão disponíveis.",
        "ui.info.1.title": "Por que existe", "ui.info.1.text": "Jogos de Cartas Colecionáveis são cultura, criatividade, competição e comunidade. Os TCG Awards dão a esse ecossistema um palco dedicado.",
        "ui.info.2.title": "Como é decidido", "ui.info.2.text": "A votação da comunidade encontra um júri independente, para que paixão e perspectiva profissional moldem o resultado juntas.",
        "ui.info.3.title": "Quem é celebrado", "ui.info.3.text": "Jogos, coleções, eventos, lojas locais, criadores, artistas, jogadores, prestadores de serviços e parcerias significativas.",
        "ui.business.li1": "Explique o desafio e a ideia.",
        "ui.business.li2": "Mostre a execução e o impacto.",
        "ui.business.li3": "Ajude o júri a avaliar a real excelência da indústria.",
        "ui.business.submit": "Envie seu caso",
        "ui.social.instagramText": "Veja impressões, anúncios e momentos da comunidade dos TCG Awards.",
        "ui.social.followInstagram": "Seguir no Instagram",
        "ui.social.facebookText": "Acompanhe publicações oficiais, novidades de eventos e anúncios para a comunidade TCG.",
        "ui.social.followFacebook": "Seguir no Facebook",
        "ui.common.loading": "Carregando…",
        "ui.common.continue": "Continuar →",
        "ui.common.back": "← Voltar",
        "ui.common.loadError": "Erro ao carregar.",
        "ui.voting.backHome": "← Voltar ao início",
        "ui.voting.backHomePage": "← Voltar à página inicial",
        "ui.voting.notFoundTitle": "Categoria não encontrada",
        "ui.voting.notFoundText": "A categoria de votação solicitada não existe.",
        "ui.voting.viewWinners": "🏆 Ver vencedores →",
        "ui.voting.notVotable": "Esta categoria não é decidida por votação da comunidade.",
        "ui.voting.idle": "Nenhuma votação está em andamento no momento. Assim que a fase de indicação ou votação começar, você poderá participar aqui.",
        "ui.voting.voteFavorite": "Vote no seu favorito",
        "ui.nom.beforeTitle": "Antes de indicar",
        "ui.nom.before1": "Esta é uma indicação da comunidade — a sua voz ajuda a definir os finalistas.",
        "ui.nom.before2": "Os resultados provisórios não são mostrados publicamente.",
        "ui.nom.before3": "Os vencedores são anunciados no palco do evento oficial.",
        "ui.nom.yourNominationFor": "A sua indicação para",
        "ui.nom.placeholder": "Digite a sua escolha…",
        "ui.nom.hint": "Digite o nome que deseja indicar. A ortografia exata não é essencial — as entradas são revisadas e agrupadas durante a avaliação.",
        "ui.nom.backInfo": "← Voltar às informações",
        "ui.nom.yourChoice": "A sua escolha",
        "ui.nom.noEntry": "Ainda não há nenhuma entrada — volte e digite a sua escolha.",
        "ui.nom.security": "Verificação de segurança",
        "ui.nom.submitting": "Enviando…",
        "ui.nom.submit": "Enviar indicação",
        "ui.nom.doneTitle": "Indicação enviada",
        "ui.nom.doneText": "Obrigado! a sua indicação foi registrada. Você pode indicar até cinco entradas diferentes nesta categoria.",
        "ui.nom.another": "Indicar outra →",
        "ui.vote.doneTitle": "Voto enviado",
        "ui.vote.doneText": "Obrigado! o seu voto foi registrado. Pode alterá-lo a qualquer momento enquanto a votação estiver aberta.",
        "ui.nom.backAll": "← Voltar às categorias",
        "ui.nom.errGeneric": "Algo deu errado. Tente novamente.",
        "ui.nom.errSubmit": "Não foi possível enviar o seu voto. Tente novamente.",
        "ui.vote.noFinalists": "Ainda não foram definidos finalistas para esta categoria.",
        "ui.vote.alreadyVoted": "Você já votou nesta categoria. A sua escolha atual está destacada — você pode alterá-la até o fim da fase.",
        "ui.vote.yourVote": "O seu voto",
        "ui.vote.saved": "✓ O seu voto foi guardado.",
        "ui.vote.saving": "Guardando…",
        "ui.vote.change": "Alterar voto",
        "ui.vote.cast": "Votar",
        "ui.vote.saveError": "Não foi possível guardar o seu voto.",
        "ui.winners.title": "Vencedores",
        "ui.winners.pending": "Os vencedores serão publicados após a cerimônia de premiação.",
        "ui.winners.picture": "Imagem",
        "ui.winners.game": "Jogo",
        "ui.winners.points": "Pontos",
        "ui.winners.pts": "pts",
        "ui.winners.place": "Lugar",
        "bc.nav.aria": "Navegação do caso de negócio",
        "bc.nav.home": "Início",
        "bc.nav.awards": "Prêmios",
        "bc.nav.submit": "Enviar",
        "bc.nav.businessCase": "Caso de negócio",
        "bc.step.overview.title": "Visão geral",
        "bc.step.overview.subtitle": "Revise as categorias e a orientação do júri",
        "bc.step.registration.title": "Registro",
        "bc.step.registration.subtitle": "Empresa, faturamento e contato",
        "bc.step.category.title": "Categoria",
        "bc.step.category.subtitle": "Escolha o caso que você está descrevendo",
        "bc.step.case.title": "Informações do caso",
        "bc.step.case.subtitle": "Descreva o caso de negócio",
        "bc.step.terms.title": "Termos",
        "bc.step.terms.subtitle": "Aceite as condições obrigatórias",
        "bc.step.attachments.title": "Anexos",
        "bc.step.attachments.subtitle": "Envie material de apoio",
        "bc.step.review.title": "Revisão",
        "bc.step.review.subtitle": "Confira tudo antes de enviar",
        "bc.help.title": "Precisa de ajuda?",
        "bc.help.text": "Revise as orientações de envio, preencha o registro, descreva o caso e envie o material de apoio para o júri.",
        "bc.help.note": "Use Salvar como rascunho a qualquer momento para gerar um link pessoal de continuação.",
        "bc.draft.status": "Status do rascunho",
        "bc.draft.loading": "Carregando rascunho salvo...",
        "bc.action.saving": "Salvando...",
        "bc.action.toRegistration": "Continuar para o registro",
        "bc.action.toCategory": "Continuar para a categoria",
        "bc.action.toCase": "Continuar para as informações do caso",
        "bc.action.toTerms": "Continuar para os termos",
        "bc.action.toAttachments": "Continuar para os anexos",
        "bc.action.toReview": "Continuar para a revisão",
        "bc.action.submit": "Enviar caso de negócio",
        "bc.action.continue": "Continuar",
        "bc.overview.title": "Envie um caso de negócio",
        "bc.overview.text": "Revise as três categorias do júri e a orientação abaixo, depois preencha o registro e descreva o seu caso. Você pode preparar vários casos nas diferentes categorias.",
        "bc.overview.ctaText": "Pronto para começar? Comece pelo registro e avance por cada etapa no seu próprio ritmo.",
        "bc.overview.ctaButton": "Iniciar registro",
        "bc.cat.brand.title": "Parceria de marca do ano",
        "bc.cat.brand.description": "Celebre a colaboração de maior impacto entre marcas, reconhecendo parcerias que elevaram a criatividade, o alcance e a relevância cultural.",
        "bc.cat.brand.s1.title": "Informações básicas",
        "bc.cat.brand.s1.description": "Informe o nome da parceria, as marcas ou organizações participantes, a duração e os mercados ou regiões-alvo.",
        "bc.cat.brand.s2.title": "Contexto estratégico",
        "bc.cat.brand.s2.description": "Descreva o objetivo da parceria, o desafio ou a oportunidade que ela abordou e o público-alvo para o qual foi criada.",
        "bc.cat.brand.s3.title": "Estrutura da parceria",
        "bc.cat.brand.s3.description": "Explique a estrutura, o escopo e os papéis envolvidos, por exemplo licenciamento, co-branding, distribuição, varejo, eventos ou colaborações com criadores.",
        "bc.cat.brand.s4.title": "Métricas de impacto",
        "bc.cat.brand.s4.description": "Apresente receita, alcance, participação, engajamento, mídia espontânea ou outras métricas que comprovem o impacto da parceria.",
        "bc.cat.brand.s5.title": "Valor estratégico",
        "bc.cat.brand.s5.description": "Destaque como a parceria gera valor de longo prazo para as marcas, o jogo e o ecossistema TCG em geral.",
        "bc.cat.campaign.title": "Campanha do ano",
        "bc.cat.campaign.description": "Reconheça a campanha de marketing mais notável do setor de TCG, com uma forte combinação de estratégia, narrativa e impacto mensurável.",
        "bc.cat.campaign.s1.title": "Informações básicas",
        "bc.cat.campaign.s1.description": "Informe o nome da campanha, as marcas ou organizações participantes, a duração e os mercados ou regiões-alvo.",
        "bc.cat.campaign.s2.title": "Objetivos da campanha",
        "bc.cat.campaign.s2.description": "Explique por que a campanha foi lançada, qual público ela visava e qual foi o conceito criativo central.",
        "bc.cat.campaign.s3.title": "Execução da campanha",
        "bc.cat.campaign.s3.description": "Descreva as ações, os canais, as parcerias com criadores, os eventos, as ativações no varejo e os formatos de conteúdo utilizados.",
        "bc.cat.campaign.s4.title": "Métricas de impacto",
        "bc.cat.campaign.s4.description": "Compartilhe o impacto de negócio, o alcance, a participação, o engajamento, a cobertura de mídia e outras métricas de sucesso.",
        "bc.cat.campaign.s5.title": "Valor estratégico",
        "bc.cat.campaign.s5.description": "Descreva o impacto de longo prazo da campanha e como ela gerou valor para a comunidade de jogadores e fãs de TCG.",
        "bc.cat.service.title": "Prestador de serviços do ano",
        "bc.cat.service.description": "Celebre a plataforma, o app ou o serviço que facilitou a vida de jogadores, lojas e organizadores e fortaleceu o ecossistema TCG.",
        "bc.cat.service.s1.title": "Informações básicas",
        "bc.cat.service.s1.description": "Cite o serviço, a plataforma, as marcas ou organizações participantes e os mercados ou regiões que atende.",
        "bc.cat.service.s2.title": "Visão geral do serviço",
        "bc.cat.service.s2.description": "Descreva a funcionalidade principal, a categoria ativa, o público-alvo e qual problema do setor o serviço resolve.",
        "bc.cat.service.s3.title": "Produto e execução",
        "bc.cat.service.s3.description": "Explique os recursos, as integrações, a escalabilidade, a confiabilidade e como funciona o suporte ao cliente ou a entrega do serviço.",
        "bc.cat.service.s4.title": "Métricas de adoção, uso e impacto",
        "bc.cat.service.s4.description": "Mostre como o sucesso é medido, quantas pessoas usam o serviço e qual impacto de negócio ele gera.",
        "bc.cat.service.s5.title": "Valor estratégico",
        "bc.cat.service.s5.description": "Destaque a relevância de longo prazo, a inovação, a diferenciação e por que o serviço é importante para o setor em geral.",
        "bc.common.total": "no total",
        "bc.common.of": "de",
        "bc.common.case": "caso",
        "bc.common.cases": "casos",
        "bc.common.selectCountry": "Selecione um país",
        "bc.common.pleaseChoose": "Selecione uma opção",
        "bc.common.yes": "Sim",
        "bc.common.no": "Não",
        "bc.reg.step.title": "Dados de registro",
        "bc.reg.step.text": "Adicione as informações da empresa, faturamento, contato e volume por categoria antes de passar para o caso de negócio em si.",
        "bc.reg.selected": "Casos selecionados atualmente",
        "bc.reg.alreadyPrepared": "Já preparados",
        "bc.reg.sec.general": "Informações gerais da empresa",
        "bc.reg.sec.billing": "Endereço de faturamento",
        "bc.reg.sec.contact": "Pessoa de contato",
        "bc.reg.sec.volume": "Volume de casos por categoria",
        "bc.reg.f.companyName": "Nome da empresa",
        "bc.reg.f.vatId": "Número de identificação fiscal (IVA)",
        "bc.reg.f.street": "Rua e número",
        "bc.reg.f.postcode": "Código postal",
        "bc.reg.f.city": "Cidade",
        "bc.reg.f.country": "País",
        "bc.reg.f.generalPhone": "Telefone geral",
        "bc.reg.f.generalEmail": "E-mail geral",
        "bc.reg.f.diffBilling": "Existe um endereço de faturamento diferente?",
        "bc.reg.f.billingCompany": "Nome da empresa de faturamento",
        "bc.reg.f.billingStreet": "Rua e número de faturamento",
        "bc.reg.f.billingPostcode": "Código postal de faturamento",
        "bc.reg.f.billingCity": "Cidade de faturamento",
        "bc.reg.f.billingCountry": "País de faturamento",
        "bc.reg.f.contactFirst": "Nome da pessoa de contato",
        "bc.reg.f.contactLast": "Sobrenome da pessoa de contato",
        "bc.reg.f.salutation": "Tratamento",
        "bc.reg.f.contactPhone": "Telefone da pessoa de contato",
        "bc.reg.f.contactEmail": "E-mail da pessoa de contato",
        "bc.reg.sal.female": "Feminino",
        "bc.reg.sal.nonbinary": "Não binário",
        "bc.reg.sal.male": "Masculino",
        "bc.reg.p.companyName": "Nome completo e juridicamente válido da empresa",
        "bc.reg.p.vatId": "Obrigatório para empresas de países da UE",
        "bc.reg.p.street": "Nome da rua e número",
        "bc.reg.p.postcode": "Código postal",
        "bc.reg.p.city": "Cidade",
        "bc.reg.p.billingCompany": "Nome da empresa de faturamento",
        "bc.reg.p.billingStreet": "Rua e número de faturamento",
        "bc.reg.p.billingPostcode": "Código postal de faturamento",
        "bc.reg.p.billingCity": "Cidade de faturamento",
        "bc.reg.p.contactFirst": "Nome",
        "bc.reg.p.contactLast": "Sobrenome",
        "bc.country.germany": "Alemanha",
        "bc.country.austria": "Áustria",
        "bc.country.switzerland": "Suíça",
        "bc.country.netherlands": "Países Baixos",
        "bc.country.belgium": "Bélgica",
        "bc.country.france": "França",
        "bc.country.italy": "Itália",
        "bc.country.spain": "Espanha",
        "bc.country.uk": "Reino Unido",
        "bc.country.us": "Estados Unidos",
        "bc.country.canada": "Canadá",
        "bc.country.other": "Outro",
        "bc.cat3.step.title": "Escolha a categoria para este caso",
        "bc.cat3.step.text": "Agora escolha a categoria exata do caso de negócio que você está descrevendo. As descrições abaixo correspondem à página de visão geral inicial.",
        "bc.cat3.warn.increase": "Aumente o número de casos desta categoria na etapa de registro ou escolha uma das categorias que você já selecionou.",
        "bc.cat3.warn.allPrepared": "Todos os casos registrados desta categoria já foram preparados. Escolha outra categoria com vagas restantes.",
        "bc.cat3.notRegistered": "Não registrado",
        "bc.cat3.remainingOf": "restantes de",
        "bc.case.step.title": "Descreva o caso de negócio",
        "bc.case.step.text": "Este é o verdadeiro núcleo da inscrição. Ofereça ao júri contexto, estratégia, detalhes de execução e impacto mensurável suficientes para que ele entenda por que este caso importa.",
        "bc.case.process.title": "Processo de inscrição e votação",
        "bc.case.process.p1": "Após enviar este formulário, a Merz Verlag GmbH & Co. KG emitirá uma fatura referente à taxa de inscrição.",
        "bc.case.process.p2": "Com essa fatura você receberá um link separado para enviar as informações do caso ou dos casos. Essas informações devem ser enviadas até 30 de junho de 2026.",
        "bc.case.process.p3": "Se os casos forem enviados após essa data ou a fatura correspondente não tiver sido paga, eles não serão encaminhados ao júri e não serão considerados.",
        "bc.case.process.p4": "De 1º a 31 de agosto de 2026 o júri analisará todos os casos e votará o Top 3. Depois, todas as empresas participantes serão informadas e o Top 3 será convidado para a TCG Awards Gala. A gala acontecerá na quinta-feira, 22 de outubro de 2026, durante a SPIEL Essen 2026.",
        "bc.case.selectedCategory": "Categoria selecionada atualmente",
        "bc.case.remaining": "Casos restantes nesta categoria",
        "bc.case.f.title": "Título do caso",
        "bc.case.f.shortDescription": "Descrição breve",
        "bc.case.f.challenge": "Contexto estratégico / desafio",
        "bc.case.f.objectives": "Objetivos",
        "bc.case.f.execution": "Execução",
        "bc.case.f.impactMetrics": "Métricas de impacto",
        "bc.case.f.strategicValue": "Valor estratégico",
        "bc.case.p.title": "Dê ao seu caso um título claro e atraente",
        "bc.case.p.shortDescription": "Resuma o seu caso em até 300 caracteres",
        "bc.case.p.challenge": "Descreva o ponto de partida, o desafio ou a oportunidade",
        "bc.case.p.objectives": "O que você queria alcançar e para quem?",
        "bc.case.p.execution": "Descreva a implementação, as ações, os canais e as medidas",
        "bc.case.p.impactMetrics": "Adicione resultados mensuráveis, tração, impacto de mídia, receita, uso ou dados de adoção",
        "bc.case.p.strategicValue": "Explique o valor de longo prazo para a marca, o produto, o público ou o ecossistema TCG em geral",
        "bc.cat.brand.short": "Parceria de marca",
        "bc.cat.campaign.short": "Campanha",
        "bc.cat.service.short": "Prestador de serviços",
        "bc.reg.noCasesSelected": "Ainda não há casos selecionados",
        "bc.nav.backHome": "Voltar ao início",
        "bc.nav.back": "Voltar",
        "bc.terms.step.title": "Termos de participação",
        "bc.terms.step.text": "Revise as condições obrigatórias antes de enviar seus arquivos. Cada caso de negócio é faturado separadamente e ainda pode ser salvo como rascunho com seu link pessoal.",
        "bc.terms.fee.label": "Taxa de inscrição",
        "bc.terms.fee.price": "500 € por caso de negócio",
        "bc.terms.fee.note": "Preço líquido. O IVA será acrescentado quando a fatura for emitida.",
        "bc.terms.required": "Obrigatório",
        "bc.terms.net.title": "Preços líquidos",
        "bc.terms.net.text": "Todos os preços indicados nesta inscrição são preços líquidos. O IVA correspondente será acrescentado quando a fatura for emitida.",
        "bc.terms.net.accept": "Entendo que todos os preços indicados neste formulário são preços líquidos.",
        "bc.terms.conditions.title": "Condições de participação",
        "bc.terms.conditions.text": "As condições de participação devem ser lidas cuidadosamente e na íntegra.",
        "bc.terms.conditions.link": "Condições de participação (abre em nova aba)",
        "bc.terms.conditions.accept": "Li as condições de participação e as aceito integralmente.",
        "bc.terms.offer.title": "Oferta juridicamente vinculativa",
        "bc.terms.offer.text": "Ao enviar este formulário, uma oferta juridicamente vinculativa é enviada à Merz Verlag como representante das partes organizadoras indicadas no §2 das condições de participação. A Merz Verlag, como representante das partes organizadoras, pode aceitar esta oferta enviando a fatura. Só então é concluído um contrato juridicamente vinculativo.",
        "bc.terms.organizers.title": "Organizadores",
        "bc.terms.organizers.text": "Os TCG Awards são organizados conjuntamente pela Merz Verlag e seus parceiros indicados no §2 das condições de participação.",
        "bc.terms.phone": "Telefone: 0201/8436207-0",
        "bc.terms.openTab": "abre em nova aba",
        "bc.terms.admin.title": "Organização administrativa:",
        "bc.terms.admin.text": "A gestão técnica do processo de inscrição e do faturamento é realizada pela Merz Verlag GmbH & Co. KG",
        "bc.terms.offer.accept": "Entendo que, ao enviar este formulário, faço uma oferta juridicamente vinculativa aos organizadores. Um contrato juridicamente válido é concluído com a fatura dos organizadores.",
        "bc.terms.cancel.title": "Taxa de cancelamento",
        "bc.terms.cancel.p1": "Um pedido de cancelamento deve ser enviado por escrito para",
        "bc.terms.cancel.p2": "O cancelamento é possível antes do faturamento e é gratuito.",
        "bc.terms.cancel.p3": "O cancelamento de inscrições de Brand Case após o faturamento é possível e está sujeito a uma taxa de processamento de 50 €. Se a fatura ainda não tiver sido paga, a fatura original será cancelada e uma nova fatura da taxa de cancelamento será emitida.",
        "bc.terms.cancel.p4": "Se a fatura já tiver sido paga, o valor pago será reembolsado menos a taxa de processamento.",
        "bc.terms.cancel.p5": "Cancelamentos após 31 de julho de 2026 não são permitidos.",
        "bc.terms.cancel.accept": "Entendo e aceito a política de cancelamento.",
        "bc.terms.privacy.title": "Política de privacidade",
        "bc.terms.privacy.p1": "Confirmo que a Merz Verlag GmbH & Co. KG pode usar e armazenar os dados que forneci para fins de processamento.",
        "bc.terms.privacy.p2": "Os dados serão usados apenas para processamento interno pela Merz Verlag GmbH & Co. KG, Card Sports League GmbH e Hana Sports & Entertainment GmbH e não serão repassados a terceiros sem consentimento.",
        "bc.terms.privacy.p3": "Mais informações sobre nosso processamento de dados podem ser encontradas em nossa política de privacidade:",
        "bc.terms.privacy.accept": "Eu concordo.",
        "bc.attachments.step.title": "Anexos",
        "bc.attachments.step.text": "Envie materiais de apoio como apresentações, relatórios, criativos, imagens, capturas de tela ou vídeos que facilitem a avaliação do caso de negócio.",
        "bc.attachments.uploadLabel": "Enviar arquivos",
        "bc.attachments.dropzoneTitle": "Arraste arquivos aqui ou clique para enviar",
        "bc.attachments.dropzoneHelp": "PDF, PPT, PPTX, JPG, PNG, MP4 - máx. 30 MB por arquivo",
        "bc.attachments.uploadedTitle": "Arquivos enviados",
        "bc.attachments.file": "arquivo",
        "bc.attachments.files": "arquivos",
        "bc.attachments.restoredWarning": "Os anexos foram restaurados a partir dos metadados do rascunho. Envie novamente os arquivos originais antes de finalizar, para que possam ser salvos no Google Drive.",
        "bc.attachments.empty": "Ainda não há arquivos enviados.",
        "bc.attachments.remove": "Remover",
        "bc.attachments.invalid": "Envie arquivos nos formatos PDF, PPT, PPTX, JPG, PNG ou MP4. Máximo de 30 MB por arquivo.",
        "bc.review.step.title": "Revisão",
        "bc.review.step.text": "Confira os dados de registro, a categoria escolhida, a descrição do caso de negócio e os arquivos enviados antes de finalizar.",
        "bc.review.registration": "Registro",
        "bc.review.edit": "Editar",
        "bc.review.noAddress": "Ainda não foi informado nenhum endereço",
        "bc.review.contact": "Contato",
        "bc.review.caseVolume": "Volume de casos",
        "bc.review.preparedSoFar": "Preparados até agora",
        "bc.review.category": "Categoria",
        "bc.review.businessCase": "Caso de negócio",
        "bc.review.noShortDescription": "Ainda não foi adicionada uma descrição breve.",
        "bc.review.noChallenge": "Ainda não foi adicionado contexto estratégico.",
        "bc.review.noObjectives": "Ainda não foram adicionados objetivos.",
        "bc.review.noExecution": "Ainda não foram adicionados detalhes de execução.",
        "bc.review.noImpactMetrics": "Ainda não foram adicionadas métricas de impacto.",
        "bc.review.noStrategicValue": "Ainda não foi adicionado valor estratégico.",
        "bc.review.feeNet": "500 € por caso de negócio, líquido",
        "bc.review.termsAccepted": "Preços líquidos, condições de participação, oferta juridicamente vinculativa, taxa de cancelamento e política de privacidade aceitos.",
        "bc.review.uploaded": "enviados",
        "bc.draft.save": "Salvar como rascunho",
        "bc.draft.saving": "Salvando rascunho...",
        "bc.draft.close": "Fechar diálogo de rascunho",
        "bc.draft.title": "Salvar como rascunho",
        "bc.draft.text": "Continue o formulário mais tarde ou compartilhe este link privado com alguém que deve concluí-lo.",
        "bc.draft.link": "Link do rascunho",
        "bc.draft.copy": "Copiar link",
        "bc.draft.note": "Os arquivos enviados são restaurados como referências. Talvez precisem ser enviados novamente mais tarde para a inscrição final.",
        "bc.draft.saved": "Rascunho salvo com sucesso. Use o link abaixo para continuar mais tarde.",
        "bc.draft.copied": "O link do rascunho foi copiado para a área de transferência.",
        "bc.draft.copyFailed": "Não foi possível copiar. Copie o link do rascunho manualmente.",
        "bc.draft.noLink": "Não foi possível gerar o link do rascunho.",
        "bc.error.loadDraft": "Não foi possível carregar o rascunho salvo.",
        "bc.error.saveDraft": "Não foi possível salvar o rascunho.",
        "bc.error.saveBusinessCase": "Não foi possível salvar o caso de negócio.",
        "bc.error.unknownBackend": "Erro desconhecido do backend.",
        "bc.success.thanks": "Obrigado!",
        "bc.success.title": "Seu caso de negócio foi salvo com sucesso.",
        "bc.success.text": "Seu registro está agora no sistema e pronto para a próxima etapa de revisão interna.",
        "bc.success.moreTitle": "Ainda há mais casos de negócio em aberto",
        "bc.success.registered": "Você registrou",
        "bc.success.inTotal": "no total e já preparou",
        "bc.success.remaining": "Ainda restam",
        "bc.success.toComplete": "para concluir a partir do mesmo registro.",
        "bc.success.details": "Detalhes do envio",
        "bc.success.submissionNumber": "Número de envio",
        "bc.success.registrationId": "ID de registro",
        "bc.success.category": "Categoria",
        "bc.success.company": "Empresa",
        "bc.success.contactEmail": "E-mail de contato",
        "bc.success.submissionDate": "Data de envio",
        "bc.success.progress": "Progresso",
        "bc.success.prepared": "preparados",
        "bc.success.createNext": "Criar próximo caso de negócio",
        "bc.success.startAnother": "Iniciar outro envio",
        "bc.success.returnHome": "Voltar ao início",
        "bc.validation.summaryStart": "Corrija",
        "bc.validation.error": "erro",
        "bc.validation.errors": "erros",
        "bc.validation.summaryEnd": "antes de continuar:",
        "bc.validation.categoryRequired": "Escolha uma categoria para o caso de negócio que você está descrevendo.",
        "bc.validation.categoryRemaining": "Escolha uma categoria com pelo menos um caso registrado restante.",
        "bc.validation.uploadBefore": "Envie pelo menos um anexo antes de",
        "bc.validation.action.continue": "continuar",
        "bc.validation.action.submit": "enviar",
        "bc.validation.reupload": "Envie novamente os anexos restaurados antes de finalizar, para que os originais possam ser salvos no Google Drive.",
        "bc.validation.requiredCaseField": "Preencha o campo obrigatório do caso:",
        "bc.validation.requiredField": "Preencha o campo obrigatório:",
        "bc.validation.validGeneralEmail": "Informe um e-mail geral válido.",
        "bc.validation.validContactEmail": "Informe um e-mail de contato válido.",
        "bc.validation.validGeneralPhone": "O telefone geral deve conter de 6 a 15 dígitos e não pode conter letras.",
        "bc.validation.validContactPhone": "O telefone de contato deve conter de 6 a 15 dígitos e não pode conter letras.",
        "bc.validation.atLeastOneCase": "Selecione pelo menos um caso entre as três categorias de casos de negócio.",
        "bc.validation.acceptNet": "Aceite a confirmação obrigatória sobre preços líquidos.",
        "bc.validation.acceptConditions": "Aceite as condições de participação obrigatórias.",
        "bc.validation.acceptOffer": "Aceite a confirmação obrigatória sobre a oferta juridicamente vinculativa.",
        "bc.validation.acceptCancellation": "Aceite a confirmação obrigatória sobre a taxa de cancelamento.",
        "bc.validation.acceptPrivacy": "Aceite a política de privacidade obrigatória.",
        "ui.footer.tagline": "Plataforma oficial dos TCG Awards",
        "ui.footer.follow": "Siga-nos",
        "ui.footer.privacy": "Privacidade",
        "ui.footer.imprint": "Ficha técnica",

        "ui.newsletter.heading": "Newsletter",
        "ui.newsletter.firstName": "Nome*",
        "ui.newsletter.lastName": "Apelido*",
        "ui.newsletter.media": "Meio / Nome da empresa",
        "ui.newsletter.link": "Ligação ao meio / canal / site",
        "ui.newsletter.country": "País",
        "ui.newsletter.email": "E-mail*",
        "ui.newsletter.privacyPre": "Ao fazê-lo, aceita o armazenamento e a utilização dos seus dados pessoais de acordo com a nossa ",
        "ui.newsletter.privacyLink": "política de privacidade",
        "ui.newsletter.privacyPost": ".",
        "ui.newsletter.subscribe": "Subscrever",

        "bc.guide.evaluation.title": "Critérios de avaliação",
        "bc.guide.evaluation.text1": "Nem todos os campos de texto têm de ser preenchidos com a mesma profundidade, mas o júri deve conseguir compreender o âmbito, o valor estratégico e o impacto mensurável do caso. Métricas relevantes, dados de apoio e anexos bem estruturados facilitam muito a avaliação.",
        "bc.guide.evaluation.text2": "Material de apoio opcional, como apresentações, vídeos, ligações, capturas de ecrã ou resumos de uma página, pode reforçar ainda mais a candidatura. Se ainda não estiver pronto, guarde o formulário como rascunho e continue mais tarde com a sua ligação pessoal.",
        "bc.guide.questions": "Questões?",
    },
    de: {
        "lang.name": "Deutsch",
        "ui.language": "Sprache",
        "ui.nav.about": "Über uns",
        "ui.common.loadError": "Fehler beim Laden.",
        "ui.nav.process": "Ablauf",
        "ui.nav.awards": "Awards",
        "ui.nav.nomination": "Nominierung",
        "ui.nav.voting": "Voting",
        "ui.nav.businessCases": "Business Cases",
        "ui.nav.news": "News",
        "hero.eyebrow": "TCG Awards 2026",
        "hero.title": "Stimme für die Menschen, Produkte und Momente, die die TCG-Kultur prägen.",
        "hero.text": "Eine globale Award-Plattform für Sammelkartenspiele, die Spitzenleistungen bei Spielen, Creators, Events, Läden, Branchenpartnern und im kompetitiven Bereich würdigt.",
        "ui.hero.nominateNow": "Jetzt nominieren",
        "ui.hero.voteNow": "Jetzt abstimmen",
        "ui.hero.exploreAwards": "Awards entdecken",
        "ui.hero.seeTimeline": "Zeitplan ansehen",
        "ui.hero.categoriesOpen": "Award-Kategorien offen für Auszeichnungen",
        "ui.hero.phases": "Phasen",
        "ui.hero.globalCommunity": "Globale Community",
        "ui.cd.nominationEnds": "Nominierung endet in",
        "ui.cd.votingEnds": "Voting endet in",
        "ui.cd.nominationStarts": "Nominierung startet in",
        "ui.cd.votingStarts": "Voting startet in",
        "ui.cd.noPhase": "Aktuell keine aktive Phase",
        "ui.cd.bcOpens": "Business Case öffnet in",
        "ui.err.noCaseSlots": "Für diese Kategorie sind keine registrierten Case-Plätze mehr verfügbar. Bitte registriere zuerst weitere Cases.",
        "ui.cd.bcClosed": "Business-Case-Einreichung geschlossen",
        "ui.cd.bcRemaining": "Verbleibend für deinen Business Case",
        "ui.cd.bcNoDeadline": "Keine Business-Case-Frist gesetzt",
        "process.eyebrow": "Voting-Zeitplan",
        "process.title": "So funktioniert der Voting-Ablauf",
        "process.text": "Der Ablauf ist einfach: starke Beiträge nominieren, sie zu einer Shortlist verdichten, die Community abstimmen lassen und die Gewinner feiern.",
        "categories.eyebrow": "Award-Kategorien",
        "categories.title": "Alle offiziellen Kategorien",
        "ui.err.generic": "Etwas ist schiefgelaufen. Bitte versuche es erneut.",
        "ui.err.network": "Der Server ist nicht erreichbar. Bitte prüfe deine Verbindung und versuche es erneut.",
        "ui.err.phaseClosed": "Die Nominierung ist derzeit geschlossen.",
        "ui.err.votingNotActive": "Das Voting ist derzeit nicht aktiv.",
        "ui.err.captcha": "Die Sicherheitsprüfung ist fehlgeschlagen. Bitte lade die Seite neu und versuche es erneut.",
        "ui.err.rateLimited": "Zu viele Anfragen. Bitte versuche es später erneut.",
        "ui.err.nominationBlocked": "Dieser Eintrag ist für die Nominierung nicht zulässig.",
        "ui.err.nominationLimit": "Du hast das Maximum an Nominierungen in dieser Kategorie erreicht.",
        "ui.err.invalidInput": "Bitte prüfe deine Eingabe und versuche es erneut.",
        "ui.err.categoryNotFound": "Diese Kategorie existiert nicht.",
        "ui.err.finalistNotFound": "Dieser Finalist existiert nicht.",
        "ui.err.submissionClosed": "Die Einreichungsfrist ist abgelaufen.",
        "ui.err.submissionNotOpen": "Das Einreichungsfenster ist noch nicht geöffnet.",
        "ui.err.invalidFileType": "Dieser Dateityp ist nicht erlaubt. Erlaubt: PDF, PPT, PPTX, JPG, PNG, MP4.",
        "ui.err.fileTooLarge": "Eine der Dateien überschreitet die maximale Größe von 30 MB.",
        "ui.err.tooManyFiles": "Es dürfen höchstens 20 Dateien hochgeladen werden.",
        "ui.err.uploadFailed": "Der Upload ist fehlgeschlagen. Bitte versuche es erneut.",
        "ui.err.driveUploadFailed": "Die Dateien konnten nicht gespeichert werden. Bitte versuche es erneut.",
        "ui.err.noCaseRegistered": "Mindestens ein Business Case muss registriert werden.",
        "ui.err.registrationNotFound": "Diese Registrierung wurde nicht gefunden.",
        "ui.err.draftNotFound": "Dieser Entwurf wurde nicht gefunden.",
        "ui.err.businessCaseNotFound": "Dieser Business Case wurde nicht gefunden.",
        "categories.text": "Alle echten Kategorien aus der aktuellen TCG-Awards-Struktur sind enthalten, gruppiert nach Abstimmungsmodell.",
        "about.eyebrow": "Über die Awards",
        "about.title": "Eine klarere Bühne für ein wachsendes Ökosystem",
        "about.text": "Die TCG Awards würdigen die Menschen und Organisationen, die Sammelkartenspiele voranbringen.",
        "business.eyebrow": "Jury-Kategorien",
        "ui.voting.winnersAvailable": "Die Votingphase wurde erfolgreich beendet. Die Gewinner sind jetzt einsehbar.",
        "business.title": "Business Case einreichen",
        "business.text": "Unternehmen, Agenturen, Verlage und Dienstleister können Business Cases für jurybasierte Kategorien wie Brand Partnership, Campaign und Service Provider of the Year einreichen.",
        "news.eyebrow": "News & Updates",
        "news.title": "Neuestes von den TCG Awards",
        "news.text": "Ankündigungen, Shortlist-Enthüllungen und Community-Momente – direkt aus unseren Social-Kanälen.",
        "newsletter.eyebrow": "Bleib auf dem Laufenden",
        "newsletter.title": "Verfolge den Weg zu den Gewinnern",
        "newsletter.text": "Erhalte Updates zu Nominierungen, Shortlist-Ankündigungen, Community-Voting und der finalen Award-Show.",
        "ui.intro.1a": "Community + Jury", "ui.intro.1b": "Faires Abstimmungsmodell",
        "ui.intro.2a": "Marken + Verlage", "ui.intro.2b": "Business Cases willkommen",
        "ui.intro.3a": "Fans + Spieler", "ui.intro.3b": "Für die ganze Szene gemacht",
        "ui.timeline.1.title": "Nominierung", "ui.timeline.1.text": "Community, Industrie und Partner bringen starke Kandidaten ins Rennen.",
        "ui.timeline.2.title": "Shortlist", "ui.timeline.2.text": "Beiträge werden geprüft und je Kategorie zu einer klaren Shortlist geformt.",
        "ui.timeline.3.title": "Community-Voting", "ui.timeline.3.text": "Fans und Spieler stimmen in den Community-Kategorien ab, die die Szene prägen.",
        "ui.timeline.4.title": "Gewinner", "ui.timeline.4.text": "Die Gewinner werden mit der globalen TCG-Community verkündet und gefeiert.",
        "ui.group.product.title": "Produkt-Exzellenz", "ui.group.product.subtitle": "Von der Community gewählt",
        "ui.group.community.title": "Community und Creator", "ui.group.community.subtitle": "Von der Community gewählt",
        "ui.group.jury.title": "Branchen- und Business-Exzellenz", "ui.group.jury.subtitle": "Von der Jury gewählt",
        "ui.group.performance.title": "Performance-Exzellenz", "ui.group.performance.subtitle": "Leistungsbasierte Bewertung",
        "ui.about.stat.categories": "Award-Kategorien",
        "ui.about.stat.phases": "Phasen",
        "ui.about.stat.reach": "Community-Reichweite",
        "ui.about.stat.edition": "Erstausgabe",
        "ui.info.1.title": "Warum es existiert", "ui.info.1.text": "Sammelkartenspiele sind Kultur, Kreativität, Wettbewerb und Community. Die TCG Awards geben diesem Ökosystem eine eigene Bühne.",
        "ui.info.2.title": "Wie entschieden wird", "ui.info.2.text": "Community-Voting trifft auf eine unabhängige Jury, damit Leidenschaft und professionelle Perspektive das Ergebnis gemeinsam formen.",
        "ui.info.3.title": "Wen es würdigt", "ui.info.3.text": "Spiele, Sets, Events, lokale Läden, Creator, Künstler, Spieler, Dienstleister und bedeutende Partnerschaften.",
        "ui.business.li1": "Erkläre die Herausforderung und die Idee.",
        "ui.business.li2": "Zeige Umsetzung und Wirkung.",
        "ui.business.li3": "Hilf der Jury, echte Branchenexzellenz zu bewerten.",
        "ui.business.submit": "Case einreichen",
        "ui.social.instagramText": "Sieh Impressionen, Ankündigungen und Community-Momente von den TCG Awards.",
        "ui.social.followInstagram": "Auf Instagram folgen",
        "ui.social.facebookText": "Folge offiziellen Beiträgen, Event-Updates und Ankündigungen für die TCG-Community.",
        "ui.social.followFacebook": "Auf Facebook folgen",
        "ui.common.loading": "Lädt…",
        "ui.common.continue": "Weiter →",
        "ui.common.back": "← Zurück",
        "ui.voting.backHome": "← Zurück zur Startseite",
        "ui.voting.backHomePage": "← Zurück zur Startseite",
        "ui.voting.notFoundTitle": "Kategorie nicht gefunden",
        "ui.voting.notFoundText": "Die angeforderte Voting-Kategorie existiert nicht.",
        "ui.voting.viewWinners": "🏆 Gewinner ansehen →",
        "ui.voting.notVotable": "Diese Kategorie wird nicht durch Community-Voting entschieden.",
        "ui.voting.idle": "Aktuell läuft keine Abstimmung. Sobald die Nominierungs- oder Voting-Phase startet, kannst du hier mitmachen.",
        "ui.voting.voteFavorite": "Stimme für deinen Favoriten",
        "ui.nom.beforeTitle": "Bevor du nominierst",
        "ui.nom.before1": "Dies ist eine Community-Nominierung – deine Stimme hilft, die Finalisten zu bestimmen.",
        "ui.nom.before2": "Zwischenergebnisse werden nicht öffentlich gezeigt.",
        "ui.nom.before3": "Die Gewinner werden auf der Bühne beim offiziellen Event bekannt gegeben.",
        "ui.nom.yourNominationFor": "Deine Nominierung für",
        "ui.nom.placeholder": "Gib deine Wahl ein…",
        "ui.nom.hint": "Gib den Namen ein, den du nominieren möchtest. Die genaue Schreibweise ist nicht entscheidend – Einträge werden bei der Auswertung geprüft und gruppiert.",
        "ui.nom.backInfo": "← Zurück zur Info",
        "ui.nom.yourChoice": "Deine Wahl",
        "ui.nom.noEntry": "Noch kein Eintrag – geh zurück und gib deine Wahl ein.",
        "ui.nom.security": "Sicherheitsprüfung",
        "ui.nom.submitting": "Wird gesendet…",
        "ui.nom.submit": "Nominierung absenden",
        "ui.nom.doneTitle": "Nominierung abgesendet",
        "ui.nom.doneText": "Danke! deine Nominierung wurde erfasst. Du kannst bis zu fünf verschiedene Einträge in dieser Kategorie nominieren.",
        "ui.nom.another": "Weitere nominieren →",
        "ui.nom.backAll": "← Zurück zu Kategorien",
        "ui.vote.doneTitle": "Stimme abgegeben",
        "ui.vote.doneText": "Danke! deine Stimme wurde erfasst. Du kannst sie jederzeit ändern, solange das Voting läuft.",
        "ui.nom.errGeneric": "Etwas ist schiefgelaufen. Bitte versuche es erneut.",
        "ui.nom.errSubmit": "Deine Stimme konnte nicht gesendet werden. Bitte versuche es erneut.",
        "ui.vote.noFinalists": "Für diese Kategorie wurden noch keine Finalisten festgelegt.",
        "ui.vote.alreadyVoted": "Du hast in dieser Kategorie bereits abgestimmt. Deine aktuelle Wahl ist markiert – du kannst sie bis zum Ende der Phase ändern.",
        "ui.vote.yourVote": "Deine Stimme",
        "ui.vote.saved": "✓ Deine Stimme wurde gespeichert.",
        "ui.vote.saving": "Wird gespeichert…",
        "ui.vote.change": "Stimme ändern",
        "ui.vote.cast": "Abstimmen",
        "ui.vote.saveError": "Konnte Stimme nicht speichern.",
        "ui.winners.title": "Gewinner",
        "ui.winners.pending": "Die Gewinner werden nach der Award-Show veröffentlicht.",
        "ui.winners.picture": "Bild",
        "ui.winners.game": "Spiel",
        "ui.winners.points": "Punkte",
        "ui.winners.pts": "Pkt.",
        "ui.winners.place": "Platz",
        "bc.nav.aria": "Business-Case-Navigation",
        "bc.nav.home": "Startseite",
        "bc.nav.awards": "Awards",
        "bc.nav.submit": "Einreichen",
        "bc.nav.businessCase": "Business Case",
        "bc.step.overview.title": "Überblick",
        "bc.step.overview.subtitle": "Kategorien und Jury-Hinweise prüfen",
        "bc.step.registration.title": "Registrierung",
        "bc.step.registration.subtitle": "Unternehmen, Rechnung und Kontakt",
        "bc.step.category.title": "Kategorie",
        "bc.step.category.subtitle": "Wähle den Case, den du beschreibst",
        "bc.step.case.title": "Case-Informationen",
        "bc.step.case.subtitle": "Beschreibe den Business Case",
        "bc.step.terms.title": "Bedingungen",
        "bc.step.terms.subtitle": "Erforderliche Bedingungen akzeptieren",
        "bc.step.attachments.title": "Anhänge",
        "bc.step.attachments.subtitle": "Unterstützendes Material hochladen",
        "bc.step.review.title": "Prüfung",
        "bc.step.review.subtitle": "Alles vor dem Absenden kontrollieren",
        "bc.help.title": "Brauchst du Hilfe?",
        "bc.help.text": "Prüfe die Einreichungshinweise, fülle die Registrierung aus, beschreibe anschließend den Case und lade unterstützendes Material für die Jury hoch.",
        "bc.help.note": "Nutze jederzeit Als Entwurf speichern, um einen persönlichen Fortsetzungslink zu erzeugen.",
        "bc.draft.status": "Entwurfsstatus",
        "bc.draft.loading": "Gespeicherter Entwurf wird geladen...",
        "bc.action.saving": "Speichert...",
        "bc.action.toRegistration": "Weiter zur Registrierung",
        "bc.action.toCategory": "Weiter zur Kategorie",
        "bc.action.toCase": "Weiter zu den Case-Informationen",
        "bc.action.toTerms": "Weiter zu den Bedingungen",
        "bc.action.toAttachments": "Weiter zu den Anhängen",
        "bc.action.toReview": "Weiter zur Prüfung",
        "bc.action.submit": "Business Case absenden",
        "bc.action.continue": "Weiter",
        "bc.overview.title": "Business Case einreichen",
        "bc.overview.text": "Sieh dir die drei Jury-Kategorien und die Hinweise unten an, fülle anschließend die Registrierung aus und beschreibe deinen Case. Du kannst mehrere Cases über die Kategorien hinweg vorbereiten.",
        "bc.overview.ctaText": "Bereit loszulegen? Beginne mit der Registrierung und arbeite dich in deinem Tempo durch die Schritte.",
        "bc.overview.ctaButton": "Registrierung starten",
        "bc.cat.brand.title": "Markenpartnerschaft des Jahres",
        "bc.cat.brand.description": "Würdige die wirkungsvollste Zusammenarbeit zwischen Marken und zeichne Partnerschaften aus, die Kreativität, Reichweite und kulturelle Relevanz gesteigert haben.",
        "bc.cat.brand.s1.title": "Grundinformationen",
        "bc.cat.brand.s1.description": "Gib den Namen der Partnerschaft, die beteiligten Marken oder Organisationen, die Dauer sowie die Zielmärkte oder -regionen an.",
        "bc.cat.brand.s2.title": "Strategischer Kontext",
        "bc.cat.brand.s2.description": "Beschreibe das Ziel der Partnerschaft, die Herausforderung oder Chance, die sie adressiert hat, und die Zielgruppe, für die sie entwickelt wurde.",
        "bc.cat.brand.s3.title": "Struktur der Partnerschaft",
        "bc.cat.brand.s3.description": "Erkläre Struktur, Umfang und beteiligte Rollen, zum Beispiel Lizenzierung, Co-Branding, Vertrieb, Retail, Events oder Creator-Kooperationen.",
        "bc.cat.brand.s4.title": "Wirkungskennzahlen",
        "bc.cat.brand.s4.description": "Zeige Umsatz, Reichweite, Teilnahme, Engagement, Earned Media oder andere Kennzahlen, die die Wirkung der Partnerschaft belegen.",
        "bc.cat.brand.s5.title": "Strategischer Wert",
        "bc.cat.brand.s5.description": "Hebe hervor, wie die Partnerschaft langfristigen Wert für die Marken, das Spiel und das gesamte TCG-Ökosystem schafft.",
        "bc.cat.campaign.title": "Kampagne des Jahres",
        "bc.cat.campaign.description": "Zeichne die herausragendste Marketingkampagne der TCG-Branche aus, mit einer starken Kombination aus Strategie, Storytelling und messbarer Wirkung.",
        "bc.cat.campaign.s1.title": "Grundinformationen",
        "bc.cat.campaign.s1.description": "Gib den Namen der Kampagne, die beteiligten Marken oder Organisationen, die Dauer sowie die Zielmärkte oder -regionen an.",
        "bc.cat.campaign.s2.title": "Kampagnenziele",
        "bc.cat.campaign.s2.description": "Erkläre, warum die Kampagne gestartet wurde, welche Zielgruppe sie ansprach und was das zentrale Kreativkonzept war.",
        "bc.cat.campaign.s3.title": "Umsetzung der Kampagne",
        "bc.cat.campaign.s3.description": "Beschreibe die eingesetzten Maßnahmen, Kanäle, Creator-Kooperationen, Events, Retail-Aktivierungen und Content-Formate.",
        "bc.cat.campaign.s4.title": "Wirkungskennzahlen",
        "bc.cat.campaign.s4.description": "Teile Business-Wirkung, Reichweite, Teilnahme, Engagement, Medienberichterstattung und weitere Erfolgskennzahlen.",
        "bc.cat.campaign.s5.title": "Strategischer Wert",
        "bc.cat.campaign.s5.description": "Beschreibe die langfristige Wirkung der Kampagne und wie sie Wert für die TCG-Spieler- und Fangemeinde geschaffen hat.",
        "bc.cat.service.title": "Dienstleister des Jahres",
        "bc.cat.service.description": "Würdige die Plattform, App oder den Service, der Spielern, Stores und Organisatoren das Leben erleichtert und das TCG-Ökosystem gestärkt hat.",
        "bc.cat.service.s1.title": "Grundinformationen",
        "bc.cat.service.s1.description": "Nenne den Service, die Plattform, die beteiligten Marken oder Organisationen sowie die Märkte oder Regionen, die er bedient.",
        "bc.cat.service.s2.title": "Service-Überblick",
        "bc.cat.service.s2.description": "Beschreibe die Kernfunktion, die aktive Kategorie, die Zielgruppe und welches Branchenproblem der Service löst.",
        "bc.cat.service.s3.title": "Produkt und Umsetzung",
        "bc.cat.service.s3.description": "Erkläre Funktionen, Integrationen, Skalierbarkeit, Zuverlässigkeit und wie Kundensupport oder Servicebereitstellung funktionieren.",
        "bc.cat.service.s4.title": "Kennzahlen zu Nutzung, Adoption und Wirkung",
        "bc.cat.service.s4.description": "Zeige, wie der Erfolg gemessen wird, wie viele Menschen den Service nutzen und welche Business-Wirkung er erzielt.",
        "bc.cat.service.s5.title": "Strategischer Wert",
        "bc.cat.service.s5.description": "Hebe die langfristige Relevanz, Innovation, Differenzierung und die Bedeutung des Service für die gesamte Branche hervor.",
        "bc.common.total": "insgesamt",
        "bc.common.of": "von",
        "bc.common.case": "Fall",
        "bc.common.cases": "Fälle",
        "bc.common.selectCountry": "Land auswählen",
        "bc.common.pleaseChoose": "Bitte wählen",
        "bc.common.yes": "Ja",
        "bc.common.no": "Nein",
        "bc.reg.step.title": "Registrierungsdaten",
        "bc.reg.step.text": "Trage die Angaben zu Unternehmen, Rechnung, Kontakt und Fallanzahl je Kategorie ein, bevor du zum eigentlichen Business Case übergehst.",
        "bc.reg.selected": "Aktuell ausgewählte Fälle",
        "bc.reg.alreadyPrepared": "Bereits vorbereitet",
        "bc.reg.sec.general": "Allgemeine Unternehmensangaben",
        "bc.reg.sec.billing": "Rechnungsadresse",
        "bc.reg.sec.contact": "Ansprechpartner",
        "bc.reg.sec.volume": "Fallanzahl je Kategorie",
        "bc.reg.f.companyName": "Firmenname",
        "bc.reg.f.vatId": "Umsatzsteuer-Identifikationsnummer",
        "bc.reg.f.street": "Straße und Hausnummer",
        "bc.reg.f.postcode": "Postleitzahl",
        "bc.reg.f.city": "Stadt",
        "bc.reg.f.country": "Land",
        "bc.reg.f.generalPhone": "Allgemeine Telefonnummer",
        "bc.reg.f.generalEmail": "Allgemeine E-Mail-Adresse",
        "bc.reg.f.diffBilling": "Gibt es eine abweichende Rechnungsadresse?",
        "bc.reg.f.billingCompany": "Firmenname für die Rechnung",
        "bc.reg.f.billingStreet": "Straße und Hausnummer für die Rechnung",
        "bc.reg.f.billingPostcode": "Postleitzahl für die Rechnung",
        "bc.reg.f.billingCity": "Stadt für die Rechnung",
        "bc.reg.f.billingCountry": "Land für die Rechnung",
        "bc.reg.f.contactFirst": "Vorname des Ansprechpartners",
        "bc.reg.f.contactLast": "Nachname des Ansprechpartners",
        "bc.reg.f.salutation": "Anrede",
        "bc.reg.f.contactPhone": "Telefonnummer des Ansprechpartners",
        "bc.reg.f.contactEmail": "E-Mail-Adresse des Ansprechpartners",
        "bc.reg.sal.female": "Weiblich",
        "bc.reg.sal.nonbinary": "Nichtbinär",
        "bc.reg.sal.male": "Männlich",
        "bc.reg.p.companyName": "Vollständiger und rechtsgültiger Firmenname",
        "bc.reg.p.vatId": "Erforderlich für Unternehmen aus EU-Ländern",
        "bc.reg.p.street": "Straßenname und Hausnummer",
        "bc.reg.p.postcode": "Postleitzahl",
        "bc.reg.p.city": "Stadt",
        "bc.reg.p.billingCompany": "Firmenname für die Rechnung",
        "bc.reg.p.billingStreet": "Straße und Hausnummer für die Rechnung",
        "bc.reg.p.billingPostcode": "Postleitzahl für die Rechnung",
        "bc.reg.p.billingCity": "Stadt für die Rechnung",
        "bc.reg.p.contactFirst": "Vorname",
        "bc.reg.p.contactLast": "Nachname",
        "bc.country.germany": "Deutschland",
        "bc.country.austria": "Österreich",
        "bc.country.switzerland": "Schweiz",
        "bc.country.netherlands": "Niederlande",
        "bc.country.belgium": "Belgien",
        "bc.country.france": "Frankreich",
        "bc.country.italy": "Italien",
        "bc.country.spain": "Spanien",
        "bc.country.uk": "Vereinigtes Königreich",
        "bc.country.us": "Vereinigte Staaten",
        "bc.country.canada": "Kanada",
        "bc.country.other": "Andere",
        "bc.cat3.step.title": "Kategorie für diesen Case wählen",
        "bc.cat3.step.text": "Wähle jetzt die genaue Kategorie für den Business Case, den du gerade beschreibst. Die Beschreibungen unten sind identisch mit der ersten Übersichtsseite.",
        "bc.cat3.warn.increase": "Erhöhe die Fallanzahl für diese Kategorie im Registrierungsschritt oder wähle eine der bereits ausgewählten Kategorien.",
        "bc.cat3.warn.allPrepared": "Alle registrierten Fälle dieser Kategorie sind bereits vorbereitet. Wähle eine andere Kategorie mit freien Plätzen.",
        "bc.cat3.notRegistered": "Nicht registriert",
        "bc.cat3.remainingOf": "übrig von",
        "bc.case.step.title": "Business Case beschreiben",
        "bc.case.step.text": "Das ist das eigentliche Herzstück der Einreichung. Gib der Jury genug Kontext, Strategie, Umsetzungsdetails und messbare Wirkung, damit sie versteht, warum dieser Case zählt.",
        "bc.case.process.title": "Einreichungs- und Abstimmungsprozess",
        "bc.case.process.p1": "Nach dem Absenden dieses Formulars stellt Merz Verlag GmbH & Co. KG eine Rechnung über die Einreichungsgebühr aus.",
        "bc.case.process.p2": "Mit dieser Rechnung erhältst du einen separaten Link zum Einreichen der Informationen zum Case bzw. zu den Cases. Diese Informationen müssen bis zum 30. Juni 2026 eingereicht werden.",
        "bc.case.process.p3": "Werden Cases nach diesem Datum eingereicht oder wurde die zugehörige Rechnung nicht bezahlt, werden sie nicht an die Jury weitergegeben und nicht berücksichtigt.",
        "bc.case.process.p4": "Vom 1. bis 31. August 2026 prüft die Jury alle Cases und wählt die Top 3. Danach werden alle teilnehmenden Unternehmen informiert und die Top 3 werden zur TCG Awards Gala eingeladen. Die Gala findet am Donnerstag, den 22. Oktober 2026, während der SPIEL Essen 2026 statt.",
        "bc.case.selectedCategory": "Aktuell ausgewählte Kategorie",
        "bc.case.remaining": "Verbleibende Fälle in dieser Kategorie",
        "bc.case.f.title": "Titel des Case",
        "bc.case.f.shortDescription": "Kurzbeschreibung",
        "bc.case.f.challenge": "Strategischer Kontext / Herausforderung",
        "bc.case.f.objectives": "Ziele",
        "bc.case.f.execution": "Umsetzung",
        "bc.case.f.impactMetrics": "Wirkungskennzahlen",
        "bc.case.f.strategicValue": "Strategischer Wert",
        "bc.case.p.title": "Gib deinem Case einen klaren, überzeugenden Titel",
        "bc.case.p.shortDescription": "Fasse deinen Case in maximal 300 Zeichen zusammen",
        "bc.case.p.challenge": "Beschreibe den Ausgangspunkt, die Herausforderung oder die Chance",
        "bc.case.p.objectives": "Was wolltest du erreichen und für wen?",
        "bc.case.p.execution": "Beschreibe die Umsetzung, Maßnahmen, Kanäle und Aktionen",
        "bc.case.p.impactMetrics": "Ergänze messbare Ergebnisse, Traction, Medienwirkung, Umsatz, Nutzung oder Adoption-Daten",
        "bc.case.p.strategicValue": "Erkläre den langfristigen Wert für die Marke, das Produkt, die Zielgruppe oder das gesamte TCG-Ökosystem",
        "bc.cat.brand.short": "Markenpartnerschaft",
        "bc.cat.campaign.short": "Kampagne",
        "bc.cat.service.short": "Dienstleister",
        "bc.reg.noCasesSelected": "Noch keine Cases ausgewählt",
        "bc.nav.backHome": "Zurück zur Startseite",
        "bc.nav.back": "Zurück",
        "bc.terms.step.title": "Teilnahmebedingungen",
        "bc.terms.step.text": "Prüfe die erforderlichen Bedingungen, bevor du deine Dateien hochlädst. Jeder Business Case wird separat berechnet und kann weiterhin über deinen persönlichen Link als Entwurf gespeichert werden.",
        "bc.terms.fee.label": "Einreichungsgebühr",
        "bc.terms.fee.price": "500 € pro Business Case",
        "bc.terms.fee.note": "Nettopreis. Die Umsatzsteuer wird bei Rechnungsstellung ergänzt.",
        "bc.terms.required": "Erforderlich",
        "bc.terms.net.title": "Nettopreise",
        "bc.terms.net.text": "Alle in dieser Einreichung genannten Preise sind Nettopreise. Die jeweilige Umsatzsteuer wird bei Rechnungsstellung ergänzt.",
        "bc.terms.net.accept": "Ich habe verstanden, dass alle in diesem Formular genannten Preise Nettopreise sind.",
        "bc.terms.conditions.title": "Teilnahmebedingungen",
        "bc.terms.conditions.text": "Die Teilnahmebedingungen sollten sorgfältig und vollständig gelesen werden.",
        "bc.terms.conditions.link": "Teilnahmebedingungen öffnen",
        "bc.terms.conditions.accept": "Ich habe die Teilnahmebedingungen gelesen und akzeptiere sie vollständig.",
        "bc.terms.offer.title": "Rechtsverbindliches Angebot",
        "bc.terms.offer.text": "Mit dem Absenden dieses Formulars wird ein rechtsverbindliches Angebot an Merz Verlag als Vertreter der veranstaltenden Parteien gemäß §2 der Teilnahmebedingungen übermittelt. Merz Verlag kann dieses Angebot als Vertreter der veranstaltenden Parteien durch Zusendung der Rechnung annehmen. Erst dadurch kommt ein rechtsverbindlicher Vertrag zustande.",
        "bc.terms.organizers.title": "Veranstalter",
        "bc.terms.organizers.text": "Die TCG Awards werden gemeinsam von Merz Verlag und den in §2 der Teilnahmebedingungen genannten Partnern veranstaltet.",
        "bc.terms.phone": "Telefon: 0201/8436207-0",
        "bc.terms.openTab": "öffnet in neuem Tab",
        "bc.terms.admin.title": "Administrative Organisation:",
        "bc.terms.admin.text": "Die technische Abwicklung des Bewerbungsprozesses und der Rechnungsstellung erfolgt durch Merz Verlag GmbH & Co. KG",
        "bc.terms.offer.accept": "Ich habe verstanden, dass ich mit dem Absenden dieses Formulars ein rechtsverbindliches Angebot an die Veranstalter abgebe. Ein rechtsgültiger Vertrag kommt mit der Rechnung der Veranstalter zustande.",
        "bc.terms.cancel.title": "Stornogebühr",
        "bc.terms.cancel.p1": "Eine Stornierung muss schriftlich eingereicht werden an",
        "bc.terms.cancel.p2": "Eine Stornierung ist vor Rechnungsstellung kostenfrei möglich.",
        "bc.terms.cancel.p3": "Eine Stornierung von Brand-Case-Einreichungen nach Rechnungsstellung ist möglich und unterliegt einer Bearbeitungsgebühr von 50 €. Wenn die Rechnung noch nicht bezahlt wurde, wird die ursprüngliche Rechnung storniert und eine neue Rechnung über die Stornogebühr ausgestellt.",
        "bc.terms.cancel.p4": "Wenn die Rechnung bereits bezahlt wurde, wird der bereits gezahlte Betrag abzüglich der Bearbeitungsgebühr erstattet.",
        "bc.terms.cancel.p5": "Stornierungen nach dem 31. Juli 2026 sind nicht möglich.",
        "bc.terms.cancel.accept": "Ich habe die Stornierungsregelung verstanden und akzeptiere sie.",
        "bc.terms.privacy.title": "Datenschutzerklärung",
        "bc.terms.privacy.p1": "Hiermit bestätige ich, dass Merz Verlag GmbH & Co. KG die von mir angegebenen Daten zum Zweck der Bearbeitung verwenden und speichern darf.",
        "bc.terms.privacy.p2": "Die Daten werden ausschließlich zur internen Bearbeitung durch Merz Verlag GmbH & Co. KG, Card Sports League GmbH und Hana Sports & Entertainment GmbH verwendet und nicht ohne Einwilligung an Dritte weitergegeben.",
        "bc.terms.privacy.p3": "Weitere Informationen zu unserer Datenverarbeitung findest du in unserer Datenschutzerklärung:",
        "bc.terms.privacy.accept": "Hiermit stimme ich zu.",
        "bc.attachments.step.title": "Anhänge",
        "bc.attachments.step.text": "Lade unterstützende Materialien wie Präsentationen, Reports, Creatives, Visuals, Screenshots oder Videos hoch, die den Business Case für die Jury leichter bewertbar machen.",
        "bc.attachments.uploadLabel": "Dateien hochladen",
        "bc.attachments.dropzoneTitle": "Dateien hierher ziehen oder zum Hochladen klicken",
        "bc.attachments.dropzoneHelp": "PDF, PPT, PPTX, JPG, PNG, MP4 - max. 30 MB pro Datei",
        "bc.attachments.uploadedTitle": "Hochgeladene Dateien",
        "bc.attachments.file": "Datei",
        "bc.attachments.files": "Dateien",
        "bc.attachments.restoredWarning": "Anhänge wurden aus den Entwurfsdaten wiederhergestellt. Bitte lade die Originaldateien vor dem Absenden erneut hoch, damit sie in Google Drive gespeichert werden können.",
        "bc.attachments.empty": "Noch keine Dateien hochgeladen.",
        "bc.attachments.remove": "Entfernen",
        "bc.attachments.invalid": "Bitte lade Dateien im Format PDF, PPT, PPTX, JPG, PNG oder MP4 hoch. Maximal 30 MB pro Datei.",
        "bc.review.step.title": "Prüfung",
        "bc.review.step.text": "Prüfe Registrierungsdaten, gewählte Kategorie, Business-Case-Beschreibung und hochgeladene Dateien, bevor du absendest.",
        "bc.review.registration": "Registrierung",
        "bc.review.edit": "Bearbeiten",
        "bc.review.noAddress": "Noch keine Adresse angegeben",
        "bc.review.contact": "Kontakt",
        "bc.review.caseVolume": "Case-Anzahl",
        "bc.review.preparedSoFar": "Bisher vorbereitet",
        "bc.review.category": "Kategorie",
        "bc.review.businessCase": "Business Case",
        "bc.review.noShortDescription": "Noch keine Kurzbeschreibung ergänzt.",
        "bc.review.noChallenge": "Noch kein strategischer Kontext ergänzt.",
        "bc.review.noObjectives": "Noch keine Ziele ergänzt.",
        "bc.review.noExecution": "Noch keine Umsetzungsdetails ergänzt.",
        "bc.review.noImpactMetrics": "Noch keine Wirkungskennzahlen ergänzt.",
        "bc.review.noStrategicValue": "Noch kein strategischer Wert ergänzt.",
        "bc.review.feeNet": "500 € pro Business Case, netto",
        "bc.review.termsAccepted": "Nettopreise, Teilnahmebedingungen, rechtsverbindliches Angebot, Stornogebühr und Datenschutzerklärung akzeptiert.",
        "bc.review.uploaded": "hochgeladen",
        "bc.draft.save": "Als Entwurf speichern",
        "bc.draft.saving": "Entwurf wird gespeichert...",
        "bc.draft.close": "Entwurfsdialog schließen",
        "bc.draft.title": "Als Entwurf speichern",
        "bc.draft.text": "Setze das Formular später fort oder teile diesen privaten Link mit jemandem, der es fertigstellen soll.",
        "bc.draft.link": "Entwurfslink",
        "bc.draft.copy": "Link kopieren",
        "bc.draft.note": "Hochgeladene Dateien werden als Referenzen wiederhergestellt. Für die finale Einreichung müssen sie später eventuell erneut hochgeladen werden.",
        "bc.draft.saved": "Entwurf erfolgreich gespeichert. Nutze den Link unten, um später fortzufahren.",
        "bc.draft.copied": "Entwurfslink wurde in die Zwischenablage kopiert.",
        "bc.draft.copyFailed": "Kopieren fehlgeschlagen. Bitte kopiere den Entwurfslink manuell.",
        "bc.draft.noLink": "Der Entwurfslink konnte nicht erstellt werden.",
        "bc.error.loadDraft": "Der gespeicherte Entwurf konnte nicht geladen werden.",
        "bc.error.saveDraft": "Der Entwurf konnte nicht gespeichert werden.",
        "bc.error.saveBusinessCase": "Der Business Case konnte nicht gespeichert werden.",
        "bc.error.unknownBackend": "Unbekannter Backend-Fehler.",
        "bc.success.thanks": "Vielen Dank!",
        "bc.success.title": "Dein Business Case wurde erfolgreich gespeichert.",
        "bc.success.text": "Deine Registrierung ist jetzt im System und bereit für die nächste interne Prüfung.",
        "bc.success.moreTitle": "Weitere Business Cases sind noch offen",
        "bc.success.registered": "Du hast",
        "bc.success.inTotal": "insgesamt registriert und bereits",
        "bc.success.remaining": "Es sind noch",
        "bc.success.toComplete": "aus derselben Registrierung offen.",
        "bc.success.details": "Einreichungsdetails",
        "bc.success.submissionNumber": "Einreichungsnummer",
        "bc.success.registrationId": "Registrierungs-ID",
        "bc.success.category": "Kategorie",
        "bc.success.company": "Unternehmen",
        "bc.success.contactEmail": "Kontakt-E-Mail",
        "bc.success.submissionDate": "Einreichungsdatum",
        "bc.success.progress": "Fortschritt",
        "bc.success.prepared": "vorbereitet",
        "bc.success.createNext": "Nächsten Business Case erstellen",
        "bc.success.startAnother": "Weitere Einreichung starten",
        "bc.success.returnHome": "Zurück zur Startseite",
        "bc.validation.summaryStart": "Bitte korrigiere",
        "bc.validation.error": "Fehler",
        "bc.validation.errors": "Fehler",
        "bc.validation.summaryEnd": "bevor du fortfährst:",
        "bc.validation.categoryRequired": "Bitte wähle eine Kategorie für den Business Case, den du beschreibst.",
        "bc.validation.categoryRemaining": "Bitte wähle eine Kategorie mit mindestens einem verbleibenden registrierten Case.",
        "bc.validation.uploadBefore": "Bitte lade mindestens einen Anhang hoch, bevor du",
        "bc.validation.action.continue": "fortfährst",
        "bc.validation.action.submit": "absendest",
        "bc.validation.reupload": "Bitte lade wiederhergestellte Anhänge vor dem Absenden erneut hoch, damit die Originaldateien in Google Drive gespeichert werden können.",
        "bc.validation.requiredCaseField": "Bitte fülle das erforderliche Case-Feld aus:",
        "bc.validation.requiredField": "Bitte fülle das erforderliche Feld aus:",
        "bc.validation.validGeneralEmail": "Bitte gib eine gültige allgemeine E-Mail-Adresse ein.",
        "bc.validation.validContactEmail": "Bitte gib eine gültige Kontakt-E-Mail-Adresse ein.",
        "bc.validation.validGeneralPhone": "Die allgemeine Telefonnummer muss 6 bis 15 Ziffern enthalten und darf keine Buchstaben enthalten.",
        "bc.validation.validContactPhone": "Die Telefonnummer des Ansprechpartners muss 6 bis 15 Ziffern enthalten und darf keine Buchstaben enthalten.",
        "bc.validation.atLeastOneCase": "Bitte wähle mindestens einen Case aus den drei Business-Case-Kategorien aus.",
        "bc.validation.acceptNet": "Bitte akzeptiere die erforderliche Bestätigung zu Nettopreisen.",
        "bc.validation.acceptConditions": "Bitte akzeptiere die erforderlichen Teilnahmebedingungen.",
        "bc.validation.acceptOffer": "Bitte akzeptiere die erforderliche Bestätigung zum rechtsverbindlichen Angebot.",
        "bc.validation.acceptCancellation": "Bitte akzeptiere die erforderliche Bestätigung zur Stornogebühr.",
        "bc.validation.acceptPrivacy": "Bitte akzeptiere die erforderliche Datenschutzerklärung.",
        "ui.footer.tagline": "Offizielle TCG-Awards-Plattform",
        "ui.footer.follow": "Folge uns",
        "ui.footer.privacy": "Datenschutz",
        "ui.footer.imprint": "Impressum",

        "ui.newsletter.heading": "Newsletter",
        "ui.newsletter.firstName": "Vorname*",
        "ui.newsletter.lastName": "Nachname*",
        "ui.newsletter.media": "Medium / Firmenname",
        "ui.newsletter.link": "Link zu Medium / Kanal / Website",
        "ui.newsletter.country": "Land",
        "ui.newsletter.email": "E-Mail*",
        "ui.newsletter.privacyPre": "Hiermit stimmst du der Speicherung und Nutzung deiner personenbezogenen Daten gemäß unserer ",
        "ui.newsletter.privacyLink": "Datenschutzerklärung",
        "ui.newsletter.privacyPost": " zu.",
        "ui.newsletter.subscribe": "Abonnieren",

        "bc.guide.evaluation.title": "Bewertungskriterien",
        "bc.guide.evaluation.text1": "Nicht jedes Textfeld muss gleich ausführlich sein, aber die Jury sollte den Umfang, den strategischen Wert und die messbare Wirkung des Cases nachvollziehen können. Relevante Kennzahlen, belastbare Daten und gut strukturierte Anhänge erleichtern die Bewertung erheblich.",
        "bc.guide.evaluation.text2": "Optionales Zusatzmaterial wie Decks, Videos, Links, Screenshots oder One-Pager kann die Einreichung weiter stärken. Wenn du noch nicht so weit bist, speichere das Formular als Entwurf und mache später über deinen persönlichen Link weiter.",
        "bc.guide.questions": "Fragen?",
    },
};
